<template>
  <div class="document-scan">
    <div class="content-wrapper">
      <!-- Left Column: Document/Lease List -->
      <div class="left-panel">
        <div class="header">
          <div class="header-title">Document search</div>
          <!-- Toggle Buttons for Documents and Leases -->
          <div class="toggle-buttons">
            <button
              class="toolbar-button"
              :class="{ active: selectedType === 'both' }"
              @click="
                this.selectedType = 'both';
                setType();
              "
            >
              Both
            </button>
            <button
              class="toolbar-button"
              :class="{ active: selectedType === 'documents' }"
              @click="
                this.selectedType = 'documents';
                setType();
              "
            >
              Docs
              <span
                @click="
                  this.selectedType = 'documents';
                  runDownload($event);
                "
                title="documents are ready to import"
                v-if="unreadDocuments > 0"
                class="notification-dot"
                >{{ unreadDocuments }}</span
              >
            </button>

            <button
              :class="{ active: selectedType === 'leases' }"
              @click="
                this.selectedType = 'leases';
                setType();
              "
            >
              Leases
              <span
                @click="
                  this.selectedType = 'leases';
                  runDownload($event);
                "
                title="documents are ready to import"
                v-if="unreadLeases > 0"
                class="notification-dot"
                >{{ unreadLeases }}</span
              >
            </button>
          </div>
        </div>
        <!-- Search Input and Clear Button -->
        <div class="search-bar">
          <input
            style="
              width: 120px;
              padding: 4px 4px;
              border: 1pt solid #ddd;
              border-radius: 4px;
            "
            ref="searchQuery"
            v-model="searchQuery"
            placeholder="Search..."
            @blur="searchBlur"
            @focus="searchFocus"
          />
          <button
            style="margin-left: 4px"
            :title="'click to expand\n search to all docs'"
            @click="expandedSearch = !expandedSearch"
            :class="['clear-button', { expandedSearch: expandedSearch }]"
          >
            <i class="fas fa-maximize"></i>
          </button>
          <button
            title="clear search"
            @click="clearSearch"
            class="clear-button"
          >
            <i class="fas fa-times"></i>
          </button>
          <button
            title="refresh data"
            @click="
              latestModifiedOn = null;
              fetchDocuments(0, this.selectedType === 'leases' ? 10 : 0);
            "
            class="clear-button"
          >
            <i class="fas fa-refresh"></i>
          </button>
          <button
            @click="runDownload"
            title="download new documents"
            class="clear-button"
          >
            <i class="fas fa-download"></i>
          </button>
          <button
            @click="hideDoneDocs"
            :title="'hide or show done\nuse [h] to toggle'"
            class="clear-button"
            :class="{ 'active-border': hideDone }"
          >
            <i class="fas fa-eye-slash"></i>
          </button>
          <button
            title="show key help"
            @click="searchHelp = !searchHelp"
            class="clear-button"
          >
            <i class="fas fa-circle-question"></i>
          </button>
          <button
            :title="'show reminders'"
            @click="showReminder"
            class="clear-button"
            :class="{ pulsating: isPulsating }"
          >
            <i class="fas fa-clock"></i>
          </button>
          <button
            title="receive payment"
            @click="receivePayment"
            class="clear-button"
          >
            <i class="fas fa-money-check"></i>
          </button>
          <button
            title="share doc"
            @click="shareURL(this.selectedDocument?.Id)"
            class="clear-button"
          >
            <i class="fas fa-share-nodes"></i>
          </button>
          <button
            title="email selected doc"
            @click="openDocumentEmailPopup"
            class="clear-button"
          >
            <i class="fas fa-envelope"></i>
          </button>
          <div class="page-selector">
            <button
              class="clear-button"
              @click="prevPage"
              :disabled="currentPage === 0"
            >
              ◄
            </button>
            <span> {{ formattedFromDate }} to {{ formattedToDate }} </span>
            <button
              class="clear-button"
              @click="nextPage"
              :disabled="!hasNextPage"
            >
              ►
            </button>
          </div>
        </div>

        <div
          class="document-list"
          :class="{ 'drag-over': dragOver }"
          @mouseover="handleTableMouseover($event)"
          @mouseleave="handleTableMouseleave($event)"
          @dragenter.prevent="handleDragEnter"
          @dragover.prevent="handleDragOver"
          @dragleave.prevent="handleDragLeave"
          @drop.prevent="handleDrop"
        >
          <div class="outer-wrapper">
            <div class="inner-wrapper">
              <table>
                <thead>
                  <tr>
                    <th
                      v-for="(col, index) in tableColumns"
                      :key="index"
                      @click="sortBy(col.key)"
                      :style="{
                        minWidth: col.width,
                        maxWidth: col.width,
                        textAlign: col.align,
                        cursor: 'pointer', // Makes the header clickable for sorting
                      }"
                    >
                      {{ col.label }}
                      <span>{{ getSortIndicator(col.key) }}</span>
                    </th>
                  </tr>
                </thead>
                <tbody style="font-size: 0.95em">
                  <tr
                    v-for="(doc, index) in filteredDocs"
                    :key="index"
                    @click="selectDocument(doc)"
                    @dblclick="setFocusEdit()"
                    :ref="`row-${index}`"
                    :class="{
                      'selected-row':
                        this.selectedDocument &&
                        doc.Id === this.selectedDocument.Id,
                    }"
                  >
                    <td
                      :title="formatDate(doc.completedOn)"
                      :class="[
                        getDueByClass(doc),
                        {
                          'lighter-text': formatDate(doc.dueBy) === '-- -- --',
                        },
                      ]"
                    >
                      {{ formatDate(doc.dueBy) }}
                    </td>

                    <td :title="doc.Id + '\n' + index">
                      {{ formatDate(doc.createdOn) }}
                    </td>
                    <td
                      :title="
                        doc.Title + '\n[r] receive payment\n[c] mark as check'
                      "
                      class="table-ellipsis"
                      style="min-width: 250px; max-width: 250px"
                    >
                      {{ doc.Title }}
                    </td>
                    <td
                      class="table-ellipsis"
                      style="min-width: 275px; max-width: 275px"
                    >
                      {{ doc.Description }}
                    </td>

                    <td :title="doc.categoryLabel" class="table-ellipsis">
                      {{ doc.categoryLabel }}
                    </td>
                    <td :title="doc.docName" class="table-ellipsis">
                      {{ doc.docName }}
                    </td>
                  </tr>
                </tbody>
              </table>
              <div
                style="margin-top: 10px; margin-bottom: 10px; font-size: 0.8em"
              >
                Not finding what you need? click
                <span
                  style="
                    cursor: pointer;
                    color: rgb(80 28 93);
                    text-decoration: underline;
                  "
                  @click="
                    expandedSearch = true;
                    hideDone = false;
                  "
                  >here</span
                >
                to expand search
              </div>
            </div>
          </div>
          <!-- Drag overlay for visual feedback -->
          <div v-if="dragOver" class="drag-overlay">
            <div class="drag-message">
              <i class="fas fa-upload"></i>
              <div>Drop PDF to upload as {{ getUploadTypeName() }}</div>
            </div>
          </div>
        </div>

        <!-- Form Section (two-column layout for Title/Description and Dropdowns) -->
        <div
          style="height: 215px"
          class="document-form"
          @mouseover="handleMouseover($event)"
          @mouseleave="handleMouseleave($event)"
        >
          <div v-if="this.selectedDocument">
            <div class="form-wrapper">
              <div class="form-row">
                <!-- Left Column (Title and Description) -->
                <div class="form-column-left">
                  <div class="form-group">
                    <label for="title">Title</label>
                    <textarea
                      ref="docTitle"
                      v-model="formData.Title"
                      rows="2"
                      @keyup="handleKeyup"
                      @focus="onFocus"
                      @blur="onBlur"
                    ></textarea>
                  </div>
                  <div class="form-group">
                    <label for="description">Description</label>
                    <textarea
                      v-model="formData.Description"
                      rows="3"
                      @keyup="handleKeyup"
                      @blur="onDescriptionBlur"
                    ></textarea>
                  </div>
                </div>

                <!-- Right Column (Dropdowns in two rows) -->
                <div class="form-column-right">
                  <div class="form-group-grid">
                    <div class="form-group">
                      <label for="category"
                        >Category (property)
                        <span class="copy-checks" style="right: -12px">
                          <input
                            type="checkbox"
                            id="copyCategory"
                            v-model="this.copyCategory"
                          />
                          <span class="copy-checks-offset">Copy</span></span
                        >
                      </label>

                      <select v-model="formData.Category" @change="handleKeyup">
                        <option
                          v-for="option in categoryOptions"
                          :key="option.value"
                          :value="option.value"
                        >
                          {{ option.label }}
                        </option>
                        <option value="new">New Category...</option>
                      </select>
                    </div>
                    <div class="form-group">
                      <label for="group"
                        >Group
                        <span class="copy-checks" style="right: -50px">
                          <input
                            type="checkbox"
                            id="copyGroup"
                            v-model="this.copyGroup"
                          />
                          <span class="copy-checks-offset">Copy</span></span
                        >
                      </label>
                      <select v-model="formData.Group" @change="handleKeyup">
                        <option
                          v-for="option in groupOptions"
                          :key="option.value"
                          :value="option.value"
                        >
                          {{ option.label }}
                        </option>
                        <option value="new">New Group...</option>
                      </select>
                    </div>
                    <div class="form-group">
                      <label for="source">
                        Source
                        <span class="copy-checks" style="right: -80px">
                          <input
                            type="checkbox"
                            id="copySource"
                            v-model="this.copySource"
                          />
                          <span class="copy-checks-offset">Copy</span></span
                        >
                      </label>
                      <select v-model="formData.Source" @change="handleKeyup">
                        <option
                          v-for="option in sourceOptions"
                          :key="option.value"
                          :value="option.value"
                        >
                          {{ option.label }}
                        </option>
                        <option value="new">New Source...</option>
                      </select>
                    </div>
                    <div class="form-group">
                      <label for="type"
                        >Type
                        <span class="copy-checks" style="right: -58px">
                          <input
                            type="checkbox"
                            id="copyType"
                            v-model="this.copyType"
                          />
                          <span class="copy-checks-offset">Copy</span></span
                        >
                      </label>
                      <select v-model="formData.Type" @change="handleKeyup">
                        <option
                          v-for="option in typeOptions"
                          :key="option.value"
                          :value="option.value"
                        >
                          {{ option.label }}
                        </option>
                        <option value="new">New Type...</option>
                      </select>
                    </div>
                  </div>
                </div>
              </div>

              <!-- Full-width Note Textarea Below Columns -->
              <div class="form-group form-note">
                <label for="note"
                  >Note:
                  <input
                    type="checkbox"
                    id="hideStickyNotes"
                    v-model="this.hideStickyNotesByDefault"
                  />
                  <span
                    style="
                      position: relative;
                      bottom: 2px;
                      margin-left: 5px;
                      font-size: 0.8em;
                      font-weight: normal;
                      padding-bottom: 10px;
                    "
                    >Hide Sticky Notes</span
                  >
                  <button
                    style="
                      margin-left: 8px;
                      font-size: 0.75em;
                      border-radius: 3px;
                      padding: 2px;
                      border: 1pt solid #ddd;
                      position: relative;
                      bottom: 3px;
                    "
                    @click="setForReceiveCheck"
                    title="mark as check [c]"
                  >
                    mark as check
                  </button>
                  <button
                    style="
                      margin-left: 8px;
                      font-size: 0.75em;
                      border-radius: 3px;
                      padding: 2px;
                      border: 1pt solid #ddd;
                      position: relative;
                      bottom: 3px;
                    "
                    @click="setForSecurityDepositCheck"
                  >
                    mark as secDep
                  </button>
                  <button
                    style="
                      margin-left: 8px;
                      font-size: 0.75em;
                      border-radius: 3px;
                      padding: 2px;
                      border: 1pt solid #ddd;
                      position: relative;
                      bottom: 3px;
                    "
                    @click="setForSecurityReturn"
                  >
                    mark as SecReturn
                  </button>
                  <button
                    style="
                      margin-left: 8px;
                      font-size: 0.75em;
                      border-radius: 3px;
                      padding: 2px;
                      border: 1pt solid #ddd;
                      position: relative;
                      bottom: 3px;
                    "
                    @click="setForLease"
                  >
                    mark as lease
                  </button>
                  <button
                    style="
                      margin-left: 8px;
                      font-size: 0.75em;
                      border-radius: 3px;
                      padding: 2px;
                      border: 1pt solid #ddd;
                      position: relative;
                      bottom: 3px;
                    "
                    @click="viewProperty"
                  >
                    view property
                  </button>
                </label>

                <textarea
                  ref="docNote"
                  v-model="formData.Note"
                  rows="3"
                  @keyup="handleKeyup"
                  @blur="handleNotesBlur"
                ></textarea>
              </div>

              <div
                v-if="selectedDocument"
                style="
                  position: absolute;
                  left: 10px;
                  bottom: 8px;
                  font-size: 0.8em;
                "
              >
                modified by:
                <span>{{
                  selectedDocument
                    ? resolveWho(selectedDocument.modifiedBy)
                    : ''
                }}</span>
              </div>
              <div class="action-button-container">
                <button title="copy" class="action-button">
                  <i @click="copyFields" class="fas fa-copy"></i>
                </button>
                <button title="paste" class="action-button">
                  <i @click="pasteFields" class="fas fa-paste"></i>
                </button>
                <button class="action-button">
                  <i @click="deleteDocument" class="fas fa-trash"></i>
                </button>
                <button @click="shareDocument" class="action-button">
                  <i class="fas fa-share"></i>
                </button>
              </div>
            </div>
          </div>
          <div v-else>No Document selected</div>
        </div>

        <div class="reminder-form">
          <div style="margin-bottom: 5px; font-weight: bold; font-size: 0.9em">
            Reminders
          </div>
          <div
            v-if="selectedDocument && documentReminders.linkedDoc"
            class="reminder-section"
          >
            <div class="reminder-column">
              <label for="dueBy">Due By</label>
              <input type="date" v-model="documentReminders.dueBy" />

              <label for="remindOn">Remind On</label>
              <input type="date" v-model="documentReminders.remindOn" />
            </div>

            <div class="reminder-column">
              <label for="priority">Priority</label>
              <select v-model="documentReminders.priority">
                <option value="1">High</option>
                <option value="2">Med</option>
                <option value="3">Low</option>
              </select>

              <label for="assignedTo">Assigned To</label>
              <select v-model="documentReminders.assignedTo">
                <option v-for="user in users" :key="user.id" :value="user.id">
                  {{ user.displayName }}
                </option>
              </select>
            </div>

            <div class="reminder-column">
              <label for="completedOn">Completed On</label>
              <input type="date" v-model="documentReminders.completedOn" />

              <label for="completedBy">Completed By</label>
              <select v-model="documentReminders.completedBy">
                <option v-for="user in users" :key="user.id" :value="user.id">
                  {{ user.displayName }}
                </option>
              </select>
            </div>
          </div>
          <div v-else>
            No document selected or no reminder
            <button @click="createNewReminder" v-if="selectedDocument">
              New
            </button>
          </div>
        </div>
      </div>

      <!-- Right Column: PDF Viewer -->
      <div class="right-panel">
        <div v-if="pdfLoading" class="pdf-loading-overlay">
          <i class="fas fa-spinner fa-spin"></i>
        </div>
        <div v-if="pdfUrl" class="pdf-viewer" ref="pdfViewer">
          <iframe
            id="pdf-frame"
            :src="pdfUrl"
            width="100%"
            height="100%"
            frameborder="0"
            @load="onPdfFrameLoad"
          ></iframe>

          <!-- Sticky Note -->
          <div
            class="sticky-note"
            v-if="selectedDocument && selectedDocument.Note && showStickyNote"
            :style="{
              top: notePosition.top + 'px',
              left: notePosition.left + 'px',
              border: isNoteHighlighted ? '2px solid blue' : '1px solid black',
            }"
            @dblclick="handleNoteDoubleClick"
          >
            <div v-html="formattedNote"></div>
            <button class="pin" @click="showStickyNote = !showStickyNote">
              <i class="fas fa-thumbtack"></i>
            </button>
          </div>
        </div>
        <div class="pdf-placeholder" v-else-if="!pdfLoading">
          <div class="pdf-placeholder-content">
            <div class="icon-container">
              <i class="fas fa-file-pdf pdf-icon"></i>
            </div>
            <p v-html="PDFplaceholderText"></p>
          </div>
        </div>
      </div>

      <!-- New Option Modal (for adding new options) -->
      <div v-if="showNewOptionModal" class="modal-overlay">
        <div class="modal-content">
          <label for="newOptionLabel">New {{ newOptionType }} Label:</label>
          <input type="text" v-model="newOptionLabel" />

          <div class="modal-actions">
            <button @click="addNewOption">Add</button>
            <button @click="closeNewOptionModal">Cancel</button>
          </div>
        </div>
      </div>
    </div>
  </div>

  <!-- Suggestions Box -->
  <div
    v-if="showSuggestions && suggestedDocs.length > 0"
    class="suggestions-box"
    :style="{ top: suggestionBoxPosition + 'px' }"
  >
    <div
      v-for="doc in suggestedDocs"
      :key="(doc.Title || '').toLowerCase()"
      class="suggestion-item"
      @click="
        this.formData.Title = doc.Title;
        this.showSuggestions = false;
      "
    >
      <span v-html="doc.highlightedTitle"></span>
    </div>
  </div>

  <!-- Footer for messages -->
  <div
    :class="['footer-message', { 'footer-message-visible': footerMessage }]"
  >
    <span>{{ footerMessage }}</span>
  </div>

  <div v-if="showOverlay" class="overlay" @click="handleOverlayClick"></div>

  <div v-if="searchHelp" class="help-overlay">
    <div class="search-help">
      <div class="tips">Tips for searching</div>
      <div style="font-size: 0.8em; margin-bottom: 5px; margin-left: 12px">
        Searcing: type a pattern in the search box. You can combine multiple
        layers of searching using commas. The Help menu bar above has other
        shortcuts you can use. You can also use keypresses defined below in the
        table.
      </div>
      <div class="table-wrapper">
        <table style="margin-bottom: 35px">
          <thead>
            <tr>
              <td style="width: 135px">Pattern or [keypress]</td>
              <td>search Result</td>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td @dblclick="usePattern('invoice')">invoice</td>
              <td>
                type a number, word..anything. It will search for 'anything'
              </td>
            </tr>
            <tr>
              <td>anything, other</td>
              <td>
                first search for 'anything', then refine the search by looking
                for 'other' within those results
              </td>
            </tr>
            <tr>
              <td style="cursor: pointer" @dblclick="usePattern(':t')">:t</td>
              <td>finds all documents scanned today.</td>
            </tr>
            <tr>
              <td style="cursor: pointer" @dblclick="usePattern(':m')">:m</td>
              <td>
                finds all documents modified today. Useful to find documents you
                changed.
              </td>
            </tr>
            <tr>
              <td style="cursor: pointer" @dblclick="usePattern(':y')">:y</td>
              <td>finds all documents scanned yesterday.</td>
            </tr>
            <tr>
              <td style="cursor: pointer" @dblclick="usePattern(':o')">:o</td>
              <td>finds all overdue documents.</td>
            </tr>
            <tr>
              <td style="cursor: pointer">alt-s</td>
              <td>
                select a row in the table; alt-s searches for some of the
                keywords to show more like that. Useful to find other documents
                for the same tenant, vendor name, etc.
              </td>
            </tr>
            <tr>
              <td style="cursor: pointer">alt-r</td>
              <td>show the reminders popup</td>
            </tr>
            <tr>
              <td style="cursor: pointer">[d]</td>
              <td>
                select a row in the table; pressing 'd' with the mouse over the
                table marks that document as 'done' in the reminders (or
                un-marks it as done, if its already marked done.)
              </td>
            </tr>
            <tr>
              <td style="cursor: pointer">[h]</td>
              <td>
                When hovering over the table, [h] hides or shows completed
                reminders.
              </td>
            </tr>
            <tr>
              <td style="cursor: pointer">[s]</td>
              <td>
                When hovering over the table, [s] toggles through sorting
                options: sort by due date, or sort by created date. reminders.
              </td>
            </tr>
            <tr></tr>
            <tr>
              <td style="cursor: pointer">[t]</td>
              <td>
                When hovering over the table, [t] shows reminders due today.
              </td>
            </tr>
            <tr>
              <td style="cursor: pointer">[c]</td>
              <td>
                Select a row, hover the table, then [c] marks the document as a
                rent check (same as "mark as check"). "mark as secDep" does the
                same for a security deposit check. After the description has
                a check number and amount (e.g. #123 $450 or $450 #123), tabbing out of
                Description opens Receive Payment. Security deposit checks also
                pre-check Security Deposit in that dialog.
              </td>
            </tr>
            <tr>
              <td style="cursor: pointer">[ESC]</td>
              <td>
                pressing Escape clears the search input and shows all documents
                again.
              </td>
            </tr>
            <tr>
              <td style="cursor: pointer">[del] or [backspace]</td>
              <td>
                If a row is selected and the cursor is hovered over the table,
                this will prompt to delete the document.
              </td>
            </tr>
          </tbody>
        </table>
      </div>
      <button class="help-close" @click="searchHelp = false">
        <i class="fas fa-close"></i>
      </button>
    </div>
  </div>

  <div v-if="showToolsPage" ref="toolsPageWrapper" class="tools-page-wrapper">
    <div class="tools-page-drag-handle">
      <i class="fas fa-grip-horizontal" aria-hidden="true"></i>
      <span>Tasks and Tools</span>
    </div>
    <ToolsPage
      :toolData="selectedRunTool"
      :canClose="true"
      @close="handleToolsPageClose"
    />
  </div>

  <!-- small confirmation dialog -->
  <div v-if="showDialogPopup" class="link-dialog-overlay">
    <div class="link-dialog-content">
      <div class="modal-title">Alert</div>
      <div style="user-select: text" v-html="dialogMessage"></div>
      <div class="link-dialog-buttons">
        <button
          v-for="option in dialogOptions"
          :key="option"
          @click="onDialogOptionSelected(option)"
          :id="option === 'Confirm' ? 'confirmButton' : ''"
        >
          {{ option }}
        </button>
      </div>
    </div>
  </div>

  <div v-if="showDocEmailPopup" class="doc-email-overlay">
    <div class="doc-email-content">
      <div class="modal-title">Email Document</div>
      <div v-if="docEmailError" class="doc-email-error-message">
        {{ docEmailError }}
      </div>
      <template v-else>
        <div class="doc-email-row">
          <div class="doc-email-label-row">
            <label for="docEmailTo">To</label>
            <button
              class="doc-email-property-btn"
              type="button"
              :class="{ active: docEmailPropertyMode }"
              @click="toggleDocEmailPropertyMode"
              :title="
                docEmailPropertyMode
                  ? 'Disable property lookup'
                  : 'Enable property lookup for To field'
              "
            >
              property
            </button>
          </div>
          <input
            id="docEmailTo"
            :type="docEmailPropertyMode ? 'text' : 'email'"
            v-model="docEmailTo"
            :placeholder="
              docEmailPropertyMode
                ? 'Search property (use commas to refine)'
                : 'recipient@example.com'
            "
            autocomplete="off"
            @input="onDocEmailToInput"
          />
          <div
            v-if="docEmailPropertyMode && docEmailPropertyMatches.length"
            class="doc-email-property-results"
          >
            <button
              v-for="property in docEmailPropertyMatches"
              :key="property.id"
              type="button"
              class="doc-email-property-result"
              @click="selectDocEmailProperty(property)"
              v-html="highlightDocEmailPropertyMatch(property.display)"
            ></button>
          </div>
        </div>
        <div class="doc-email-row">
          <label for="docEmailSubject">Subject</label>
          <input
            id="docEmailSubject"
            type="text"
            v-model="docEmailSubject"
            placeholder="email subject"
            autocomplete="off"
          />
        </div>
        <div class="doc-email-row">
          <label for="docEmailMessage">Message</label>
          <textarea
            id="docEmailMessage"
            v-model="docEmailMessage"
            rows="5"
            placeholder="email message"
          ></textarea>
        </div>
      </template>
      <div class="doc-email-buttons">
        <button
          v-if="!docEmailError"
          :disabled="sendingDocEmail"
          @click="sendDocumentEmail"
        >
          <span v-if="sendingDocEmail" class="doc-email-button-spinner"></span>
          <span v-else>Send</span>
        </button>
        <button :disabled="sendingDocEmail" @click="closeDocumentEmailPopup">
          {{ docEmailError ? 'OK' : 'Cancel' }}
        </button>
      </div>
    </div>
  </div>

  <div class="receive-payment" :class="{ 'modal-active': showReceivePayment }">
    <div v-if="showReceivePayment" class="modal">
      <!-- Modal Content -->
      <div
        ref="receivePaymentDialog"
        class="modal-content"
        :style="receivePaymentDialogStyle"
      >
        <div
          class="modal-title"
          @mousedown.stop.prevent="startReceivePaymentDrag"
        >
          Receive Payment
        </div>
        <button
          @click="receivePaymentRaised = !receivePaymentRaised"
          :title="
            receivePaymentRaised
              ? 'Move dialog down'
              : 'Move dialog up to reveal document'
          "
          style="
            position: absolute;
            top: 10px;
            right: 38px;
            background: transparent;
            border: none;
            color: #fff;
            cursor: pointer;
            font-size: 1em;
          "
        >
          <i
            :class="
              receivePaymentRaised ? 'fas fa-arrow-down' : 'fas fa-arrow-up'
            "
          ></i>
        </button>
        <button
          @click="closeModal"
          style="position: absolute; top: 10px; right: 13px"
        >
          X
        </button>
        <!-- Search Input and Check Navigation Row -->
        <div class="search-row">
          <input
            autocomplete="new-password"
            ref="paymentSearch"
            type="text"
            v-model="searchTerm"
            placeholder="Search by address, name, or email"
            @input="searchPeopleProperties"
            class="search-box"
          />
          <button
            type="button"
            class="previous-tenant-btn"
            :class="{ active: includePreviousTenants }"
            title="Include tenants from older leases"
            @click="togglePreviousTenants"
          >
            Previous tenant
          </button>
          <!-- Check navigation (inline with search) -->
          <div v-if="selectedResult" class="check-navigation">
            <button
              class="nav-arrow nav-arrow-left"
              :disabled="currentPaymentIndex === 0"
              @click="prevPayment"
              title="Previous check"
            >
              <i class="fas fa-chevron-left"></i>
            </button>
            <span class="check-indicator">
              {{ currentPaymentIndex + 1 }}/{{ payments.length }}
              <button
                v-if="payments.length > 1"
                class="remove-check-btn"
                @click="removeCurrentPayment"
                title="Remove this check"
              >
                <i class="fas fa-trash-alt"></i>
              </button>
            </span>
            <button
              class="nav-arrow nav-arrow-right"
              @click="nextPayment"
              :title="
                currentPaymentIndex === payments.length - 1
                  ? 'Add another check'
                  : 'Next check'
              "
            >
              <i
                :class="
                  currentPaymentIndex === payments.length - 1
                    ? 'fas fa-plus'
                    : 'fas fa-chevron-right'
                "
              ></i>
            </button>
          </div>
        </div>

        <!-- Search Results -->
        <div class="search-results" v-if="searchResults.length">
          <div
            v-for="(result, index) in filteredPeopleProperties"
            :key="index"
            @click="selectResult(result)"
            :class="[
              'result-item',
              { 'selected-result': result === selectedResult },
            ]"
          >
            <span
              v-html="highlight(formatReceivePaymentResult(result))"
            ></span>
          </div>
        </div>

        <!-- Form Inputs (Initially Hidden) -->
        <div v-if="selectedResult" class="payment-inputs-wrapper">
          <!-- Inputs in a horizontal row -->
          <div class="payment-inputs">
            <div class="input-group" style="flex: none; width: 110px">
              <label for="checkNumber">Check Number</label>
              <input
                autocomplete="new-password"
                type="text"
                v-model="checkNumber"
                id="checkNumber"
                ref="checkNumber"
              />
            </div>

            <div class="input-group">
              <label for="checkDate">Check Date</label>
              <input
                autocomplete="new-password"
                type="date"
                v-model="checkDate"
                id="checkDate"
                ref="checkDate"
                @focus="removeIconFocus"
                @blur="propagatePaymentStatusAndMethod"
              />
            </div>

            <div class="input-group">
              <label for="amount">Amount</label>
              <input
                autocomplete="new-password"
                type="number"
                v-model="amount"
                id="amount"
              />
            </div>

            <div class="input-group">
              <label for="paymentStatus">Status</label>
              <select
                v-model="paymentStatus"
                id="paymentStatus"
                @blur="propagatePaymentStatusAndMethod"
              >
                <option value="0"></option>
                <option value="1">Early</option>
                <option value="2">Late</option>
                <option value="3">Rejected</option>
                <option value="4">Returned</option>
              </select>
            </div>

            <div class="input-group">
              <label for="paymentMethod">Payment Method</label>
              <select
                v-model="paymentMethod"
                id="paymentMethod"
                @blur="propagatePaymentStatusAndMethod"
                @keydown.tab="advancePaymentOnTab"
              >
                <option value="1">Paper Check</option>
                <option value="2">Electronic</option>
                <option value="3">Cashier's Check</option>
                <option value="4">Other</option>
                <option value="5">Money Order</option>
                <option value="6">Billpayment Svc</option>
              </select>
            </div>
          </div>
          <div>
            <input
              style="width: 20px"
              type="checkbox"
              v-model="isSecurityDeposit"
            />
            Security Deposit

            <!-- Action buttons positioned below the inputs -->
            <div class="action-buttons">
              <button :disabled="isSubmitting" @click="submitPayment(true)">
                Submit / Email
              </button>
              <button :disabled="isSubmitting" @click="submitPayment(false)">
                Submit Only
              </button>
            </div>
          </div>
        </div>
      </div>

      <!-- Close Button -->
      <button class="modal-close is-large" @click="closeModal"></button>
    </div>
  </div>

  <!-- Lease Search iframe overlay -->
  <div
    v-if="propertyIframeUrl"
    class="property-iframe-overlay"
    @click.self="propertyIframeUrl = null"
  >
    <div class="property-iframe-container">
      <div class="property-iframe-title-bar">
        <span>{{ propertyIframeTitle }}</span>
        <button
          class="property-iframe-close-btn"
          @click="propertyIframeUrl = null"
        >
          <i class="fas fa-times"></i>
        </button>
      </div>
      <div class="property-iframe-scroll-wrapper">
        <div
          v-if="propertyIframeLoading"
          class="property-iframe-spinner-overlay"
        >
          <i class="fas fa-spinner fa-spin"></i>
          <span>Loading lease…</span>
        </div>
        <iframe
          :src="propertyIframeUrl"
          class="property-lease-iframe"
          @load="propertyIframeLoading = false"
        ></iframe>
      </div>
    </div>
  </div>
</template>

<script>
import axios from '@/axiosConfig';
import debounce from 'lodash/debounce';
import eventBus from './eventBus.js';
import ToolsPage from './ToolsPage.vue'; // Adjust the path if necessary
import io from 'socket.io-client';
import { makeDraggable } from '@/utils/makeDraggable';
import {
  parseCheckPaymentsFromDescription as parseCheckPayments,
  isReadyToAutoReceivePayment as isReadyToAutoReceive,
  isSecurityDepositCheckTitle,
} from '@/utils/documentScanReceivePayment';

export default {
  components: {
    ToolsPage,
  },
  data() {
    // Constants
    const DEFAULT_SORT_KEY = 'createdOn';
    const DEFAULT_SORT_ORDER = -1;
    const DEFAULT_PAYMENT_METHOD = '1'; // Paper Check
    const DEFAULT_PRIORITY = 2; // Medium priority

    return {
      // document table pagination
      currentPage: 0,
      itemsPerPage: 500,
      expandedSearch: false,

      // Form submission and document state
      isSubmitting: false,
      enableOverwriteChecking: true,
      myUserId: null,
      noteWasDoubleClicked: false,
      docLastUpdatedOn: null,
      updateCheckInterval: null,

      // Counters and statuses
      unreadDocuments: 0,
      unreadLeases: 0,
      statusMessage: '',
      footerMessage: '',

      // Search and selection
      isSearchFocused: false,
      autoSelected: false,
      selectedRowIndex: 0,
      selectedType: 'documents',
      dragOver: false,
      searchQuery: '',
      debouncedSearchQuery: null,
      searchTerm: '',
      searchResults: [],
      searchResultsMode: null,
      includePreviousTenants: false,
      selectedResult: null,

      // Document and table handling
      sortKey: DEFAULT_SORT_KEY,
      sortOrder: DEFAULT_SORT_ORDER,
      importedDocs: [],
      selectedDocument: null,
      selectedDocumentAfterSave: null,
      pdfUrl: null,
      pdfLoading: false,
      tableColumns: [
        { label: 'Due By', width: '55px', align: 'center', key: 'dueBy' },
        { label: 'Created', width: '65px', align: 'center', key: 'createdOn' },
        { label: 'Title', width: '240px', align: 'left', key: 'Title' },
        {
          label: 'Description',
          width: '150px',
          align: 'left',
          key: 'Description',
        },
        { label: 'Property', width: '100px', align: 'left', key: '' },
        { label: 'DocName', width: '100px', align: 'left', key: 'docName' },
      ],

      // Form data
      formData: {
        Title: '',
        Description: '',
        Category: null,
        Group: null,
        Source: null,
        Type: null,
        Note: '',
      },

      // Options and dropdowns
      categoryOptions: [],
      groupOptions: [],

      // Property iframe overlay
      propertyIframeUrl: null,
      propertyIframeTitle: '',
      propertyIframeLoading: true,
      sourceOptions: [],
      typeOptions: [],

      // Editing and modal states
      showNewOptionModal: false,
      addingNewOption: false,
      newOptionType: '',
      newOptionLabel: '',
      isEditing: false,

      // copy paste support for document properties
      copyCategory: false,
      copyGroup: false,
      copySource: false,
      copyType: false,
      copiedProperties: {},

      // Sticky notes and overlays
      notePosition: { top: 0, left: 0 },
      showStickyNote: false,
      hideStickyNotesByDefault: true,
      isNoteHighlighted: false,
      showOverlay: false,
      showSuggestions: false,
      suggestionBoxPosition: 0,
      typingTimeout: null,
      searchHelp: false,

      // Tool page and dialog states
      showToolsPage: false,
      toolsPageDragCleanup: null,
      toolData: {},
      showDialogPopup: false,
      showDocEmailPopup: false,
      docEmailTo: '',
      docEmailSubject: '',
      docEmailMessage: '',
      docEmailPropertyMode: false,
      docEmailPropertyMatches: [],
      tenantPropertyEmailRows: [],
      docEmailError: '',
      sendingDocEmail: false,
      mouseOverTable: false,
      hideDone: true,

      // Document reminders structure
      documentReminders: {
        linkedDoc: null,
        dueBy: null,
        remindOn: null,
        priority: DEFAULT_PRIORITY,
        assignedTo: null,
        completedOn: null,
        completedBy: null,
      },

      // User-related data
      users: [],

      // Payment functionalities
      showReceivePayment: false,
      receivePaymentRaised: false,
      isReceivePaymentDragging: false,
      receivePaymentDragOffset: { x: 0, y: 0 },
      receivePaymentPosition: { top: null, left: null },
      checkNumber: '',
      checkDate: '',
      amount: '',
      paymentStatus: '0',
      paymentMethod: DEFAULT_PAYMENT_METHOD,
      // default purpose for payment = 1 (Rent)
      isSecurityDeposit: false,
      purpose: 1,
      // Multi-check support
      payments: [],
      currentPaymentIndex: 0,
      socket: null,
      isPulsating: false,
      reminderPulsationDisabled: false,
      pulsationInterval: null,
      checkReminderHour: 11,
      PDFplaceholderText:
        '&larr; Select a document to view it as PDF, or drag and drop a PDF file onto the document table to upload a new one.',
    };
  },

  created() {
    document.title = 'Documents';
    this.fetchUsers();

    this.fetchUnreadCounts(); // Initial fetch
    this.timer = setInterval(this.fetchUnreadCounts, 120000); // Fetch every 60 seconds

    eventBus.on('update:showReminderDialog', (status) => {
      // console.log('DocumentScan: got closed', status);
      if (!status)
        this.fetchDocuments(
          0,
          this.selectedType === 'leases'
            ? 10
            : this.selectedType === 'both'
              ? '-'
              : 0,
        );
    });

    eventBus.on('select-document', this.handleSelectDocument);

    window.addEventListener('keydown', this.handleKeyDown);
  },

  computed: {
    filteredPeopleProperties() {
      const filteredResults = this.includePreviousTenants
        ? this.searchResults
        : this.searchResults.filter((result) => result.endsOn !== null);

      // Remove duplicates based on peopleId and leaseId combination
      const uniqueResults = [];
      const seen = new Set();

      for (const result of filteredResults) {
        const key = `${result.peopleId}-${result.leaseId}`;
        if (!seen.has(key)) {
          seen.add(key);
          uniqueResults.push(result);
        }
      }

      // If there's no searchTerm, return the unique filtered results directly
      if (!this.searchTerm) return uniqueResults;

      const searchTermLower = this.searchTerm.toLowerCase();

      // Filter the results based on searchTerm
      return uniqueResults.filter((result) => {
        const concatenatedString =
          `${result.fullName} ${result.address} ${result.email}`.toLowerCase();
        return concatenatedString.includes(searchTermLower);
      });
    },

    formattedNote() {
      return this.selectedDocument.Note.replace(/\n/g, '<br>');
    },
    suggestedDocs() {
      const formTitle = this.formData.Title.toLowerCase();

      // Return empty array if title is empty
      if (!formTitle) return [];

      // Escape special regex characters to prevent regex errors
      const escapeRegExp = (string) => {
        return string.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
      };
      const safeSearchTerm = escapeRegExp(this.formData.Title);
      const regex = new RegExp(`(${safeSearchTerm})`, 'ig');

      // One suggestion per unique title (case-insensitive)
      const seenTitles = new Set();
      const unique = [];

      for (const doc of this.filteredDocs) {
        const rawTitle = doc.Title || '';
        const docTitle = rawTitle.toLowerCase();
        if (!docTitle.includes(formTitle) || docTitle === formTitle) continue;
        if (seenTitles.has(docTitle)) continue;
        seenTitles.add(docTitle);

        unique.push({
          ...doc,
          highlightedTitle: rawTitle.replace(
            regex,
            '<span class="highlight">$1</span>',
          ),
        });

        if (unique.length >= 20) break;
      }

      return unique;
    },

    formattedFromDate() {
      const createdOn = this.sortedDocs[0]?.createdOn;
      if (!createdOn) return '';

      const date = new Date(createdOn);

      // Extract month, day, and year
      const month = String(date.getMonth() + 1).padStart(2, '0'); // Months are zero-indexed, so add 1
      const day = String(date.getDate()).padStart(2, '0');
      const year = date.getFullYear() % 100;

      return `${month}/${day}/${year}`;
    },

    formattedToDate() {
      const createdOn = this.sortedDocs[this.sortedDocs.length - 1]?.createdOn;
      if (!createdOn) return '';

      const date = new Date(createdOn);

      // Extract month, day, and year
      const month = String(date.getMonth() + 1).padStart(2, '0'); // Months are zero-indexed, so add 1
      const day = String(date.getDate()).padStart(2, '0');
      const year = date.getFullYear() % 100;

      return `${month}/${day}/${year}`;
    },

    hasNextPage() {
      return (
        this.currentPage <
        Math.ceil(this.importedDocs.length / this.itemsPerPage) - 1
      );
    },

    receivePaymentDialogStyle() {
      return {
        top:
          this.receivePaymentPosition.top != null
            ? `${this.receivePaymentPosition.top}px`
            : this.receivePaymentRaised
              ? '122px'
              : '570px',
        left:
          this.receivePaymentPosition.left != null
            ? `${this.receivePaymentPosition.left}px`
            : '828px',
      };
    },

    sortedDocs() {
      // Determine start and end indices based on expandedSearch flag
      let start, end;

      if (this.expandedSearch) {
        start = 0;
        end = this.importedDocs.length;
      } else {
        start = this.currentPage * this.itemsPerPage;
        end = start + this.itemsPerPage;
      }

      // Slice the importedDocs array accordingly
      const slicedArray = this.importedDocs.slice(start, end);

      if (!this.sortKey) {
        return slicedArray; // Return unsorted but sliced data if no sort key
      }

      // Sort the sliced array
      const result = [...slicedArray].sort((a, b) => {
        const [valueA, valueB] = [a[this.sortKey], b[this.sortKey]].map(
          (val) => (val ? new Date(val) : null),
        );

        if (!valueA) return 1;
        if (!valueB) return -1;

        return (valueA - valueB) * this.sortOrder;
      });

      return result;
    },

    filteredDocs() {
      if (!this.sortedDocs) {
        return [];
      }

      const searchQuery = this.debouncedSearchQuery || ''; // Use debounced query

      const todayStr = new Date().toISOString().slice(0, 10);

      let filterFn = () => true; // Default to no filtering

      if (searchQuery.startsWith(':')) {
        filterFn = this.getSearchFilterFunction(searchQuery, todayStr);
      } else if (searchQuery) {
        const tokens = searchQuery.split(',').map((token) => token.trim());
        filterFn = this.getTokenFilterFunction(tokens);
      }

      const result = this.sortedDocs
        .filter((doc) => !this.hideDone || !doc.completedOn)
        .filter(filterFn);

      return result;
    },
  },

  methods: {
    // Add to methods section
    checkReminderPulsation() {
      const now = new Date();
      const hours = now.getHours();
      const minutes = now.getMinutes();

      // Get stored disabled state from localStorage
      const storedDisabled = localStorage.getItem('reminderPulsationDisabled');
      const storedDate = localStorage.getItem('reminderPulsationDate');
      const today = now.toDateString();

      // Reset the disabled state if it's a new day
      if (storedDisabled === 'true' && storedDate !== today) {
        localStorage.removeItem('reminderPulsationDisabled');
        localStorage.removeItem('reminderPulsationDate');
        this.reminderPulsationDisabled = false;
      }

      // Check if it's time to pulsate the reminder button and pulsation isn't disabled
      if (
        hours >= this.checkReminderHour &&
        minutes > 0 &&
        !this.reminderPulsationDisabled
      ) {
        this.startPulsation();
      } else if (this.isPulsating && (hours !== 11 || minutes > 0)) {
        this.stopPulsation();
      }
    },

    startPulsation() {
      this.isPulsating = true;
    },

    stopPulsation() {
      this.isPulsating = false;
      this.reminderPulsationDisabled = true;

      // Store the disabled state in localStorage with today's date
      localStorage.setItem('reminderPulsationDisabled', 'true');
      localStorage.setItem('reminderPulsationDate', new Date().toDateString());
    },

    // Update the showReminder method to stop pulsation when clicked during pulsation
    showReminder() {
      if (this.isPulsating) {
        this.stopPulsation();
      }
      // console.log('Emitting update:showReminderDialog from DocumentScan');
      eventBus.emit('update:showReminderDialog', true);
    },

    async shareURL(id) {
      if (!id) return;
      try {
        // Show a dialog asking for desktop or mobile sharing
        const result = await this.showDialog(
          'Do you want to share this document for desktop or mobile viewing?',
          ['Desktop', 'Mobile', 'Cancel'],
        );

        if (result === 'Cancel') return;

        const basePath = result === 'Desktop' ? '/documents' : '/mdocs';
        let url = new URL(window.location.origin + basePath);

        // Append the search parameter
        url = url + '?search=:' + id;

        if (navigator.clipboard && window.isSecureContext) {
          await navigator.clipboard.writeText(url.toString());
          this.footerMessage = `URL for ${result.toLowerCase()} viewing copied to clipboard.`;
        } else {
          const clipboardMessage = `URL copied to clipboard: ${url.toString()}`;
          await this.showDialog(clipboardMessage, ['OK']);
        }
      } catch (error) {
        console.error('Error processing the URL:', error);
      }
    },

    // Drag and Drop methods for PDF upload
    handleDragEnter(event) {
      event.preventDefault();
      event.stopPropagation();
      event.dataTransfer.dropEffect = 'copy';
      this.dragOver = true;
    },

    handleDragOver(event) {
      event.preventDefault();
      event.stopPropagation();
      event.dataTransfer.dropEffect = 'copy';
      this.dragOver = true;
    },

    handleDragLeave(event) {
      event.preventDefault();
      event.stopPropagation();
      // Only hide drag over if we're leaving the container completely
      if (!event.currentTarget.contains(event.relatedTarget)) {
        this.dragOver = false;
      }
    },

    async handleDrop(event) {
      event.preventDefault();
      event.stopPropagation();
      this.dragOver = false;
      this.selectedType = 'both';

      const files = Array.from(event.dataTransfer.files);
      const pdfFiles = files.filter((file) => file.type === 'application/pdf');

      if (pdfFiles.length === 0) {
        this.updateFooterMessage('Please drop PDF file(s)', 'error');
        return;
      }

      // Upload multiple PDFs sequentially
      const total = pdfFiles.length;
      let uploaded = 0;
      for (const pdfFile of pdfFiles) {
        uploaded++;
        if (total > 1) {
          this.updateFooterMessage(`Uploading ${uploaded} of ${total}...`);
        }
        await this.uploadPDF(pdfFile, total > 1 ? uploaded : null);
      }

      if (total > 1) {
        this.updateFooterMessage(`${total} PDF files uploaded successfully`);
      }
    },

    generateDocumentTitle(sequenceNum = null) {
      const now = new Date();
      const month = String(now.getMonth() + 1).padStart(2, '0');
      const day = String(now.getDate()).padStart(2, '0');
      const year = String(now.getFullYear()).slice(-2);
      const hour = String(now.getHours()).padStart(2, '0');
      const minute = String(now.getMinutes()).padStart(2, '0');
      const timestamp = `${month}${day}${year}-${hour}${minute}`;

      const typeName = this.getUploadTypeName().toLowerCase();
      const suffix = sequenceNum ? `-${sequenceNum}` : '';
      return `${typeName} ${timestamp}${suffix}`;
    },

    getUploadTypeName() {
      switch (this.selectedType) {
        case 'both':
          return 'Document';
        case 'documents':
          return 'Document';
        case 'leases':
          return 'Lease';
        default:
          return 'Document';
      }
    },

    async uploadPDF(file, sequenceNum = null) {
      this.updateFooterMessage('Uploading PDF...');

      try {
        const documentTitle = this.generateDocumentTitle(sequenceNum);
        const year = new Date().getFullYear().toString();
        const relativePath = `Cabinet/${year}/${this.selectedType === 'leases' ? 'Leases' : 'Documents'}`;

        // Use chunked upload for files > 512KB to bypass nginx limits
        const CHUNK_SIZE = 512 * 1024; // 512KB chunks
        if (file.size > CHUNK_SIZE) {
          await this.uploadPDFChunked(file, documentTitle, relativePath);
        } else {
          // Small files can use the simple upload
          const formData = new FormData();
          formData.append('file', file);
          formData.append('documentTitle', documentTitle);
          formData.append('relativePath', relativePath);
          await axios.post('/api/upload-lease-document', formData);
        }

        this.updateFooterMessage(
          `PDF uploaded successfully as "${documentTitle}"`,
        );

        // Refresh the document list to show the newly uploaded document
        this.fetchDocuments(0, this.selectedType === 'leases' ? 10 : 0);
      } catch (error) {
        console.error('Error uploading file:', error);
        this.updateFooterMessage('Error uploading PDF file');
      }
    },

    async uploadPDFChunked(file, documentTitle, relativePath) {
      const CHUNK_SIZE = 512 * 1024; // 512KB chunks
      const totalChunks = Math.ceil(file.size / CHUNK_SIZE);

      // Step 1: Initialize the upload session
      const initResponse = await axios.post('/api/upload-chunk-init', {
        fileName: file.name,
        fileSize: file.size,
        totalChunks,
        documentTitle,
        relativePath,
      });
      const { uploadId } = initResponse.data;

      // Step 2: Upload chunks sequentially
      for (let i = 0; i < totalChunks; i++) {
        const start = i * CHUNK_SIZE;
        const end = Math.min(start + CHUNK_SIZE, file.size);
        const chunk = file.slice(start, end);

        const formData = new FormData();
        formData.append('chunk', chunk);
        formData.append('uploadId', uploadId);
        formData.append('chunkIndex', i.toString());

        await axios.post('/api/upload-chunk', formData);

        // Update progress
        const progress = Math.round(((i + 1) / totalChunks) * 100);
        this.updateFooterMessage(`Uploading PDF... ${progress}%`);
      }

      // Step 3: Complete the upload (assemble chunks on server)
      await axios.post('/api/upload-chunk-complete', { uploadId });
    },

    parseCheckPaymentsFromDescription(description) {
      return parseCheckPayments(description);
    },

    isReadyToAutoReceivePayment() {
      return isReadyToAutoReceive({
        showReceivePayment: this.showReceivePayment,
        selectedDocument: this.selectedDocument,
        formData: this.formData,
        typeOptions: this.typeOptions,
        sourceOptions: this.sourceOptions,
        groupOptions: this.groupOptions,
      });
    },

    async onDescriptionBlur() {
      if (!this.isReadyToAutoReceivePayment()) return;
      await this.saveFormData();
      if (!this.isReadyToAutoReceivePayment()) return;
      this.receivePayment();
    },

    setForReceiveCheck() {
      // Souroe = dropbox; Group = Rent; Type  =Check;
      if (this.selectedDocument) {
        this.showStickyNote = false;
        this.formData.Source = 9;
        this.formData.Group = 14;
        this.formData.Type = 8;
        // Set default title — if 25th or later, use the following month
        const now = new Date();
        let targetDate = now;
        if (now.getDate() >= 25) {
          targetDate = new Date(now.getFullYear(), now.getMonth() + 1, 1);
        }
        const monthName = targetDate.toLocaleString('en-US', { month: 'long' });
        const year2Digit = String(targetDate.getFullYear()).slice(-2);
        this.formData.Title = `Rent check for ${monthName} ${year2Digit}`;
        this.saveFormData();
        this.$nextTick(() => {
          const el = this.$refs.docTitle;
          if (el) {
            el.focus();
            el.setSelectionRange(el.value.length, el.value.length);
          }
        });
      }
    },
    setForSecurityDepositCheck() {
      if (this.selectedDocument) {
        this.showStickyNote = false;
        this.formData.Source = 9;
        this.formData.Group = 31;
        this.formData.Type = 8;
        this.formData.Title = 'Security Deposit check for ';
        this.saveFormData();
        this.$nextTick(() => {
          const el = this.$refs.docTitle;
          if (el) {
            el.focus();
            el.setSelectionRange(el.value.length, el.value.length);
          }
        });
      }
    },
    findOptionValue(options, label) {
      if (!options?.length || !label) return null;
      const lower = label.toLowerCase();
      const match = options.find(
        (o) => o.label && o.label.toLowerCase() === lower,
      );
      return match ? match.value : null;
    },
    parseCategoryFromTitleEnd(title) {
      // Match the longest category (property) label that ends the title
      if (!title || !this.categoryOptions?.length) return null;
      const titleLower = title.trim().toLowerCase();
      let bestValue = null;
      let bestLen = 0;
      for (const opt of this.categoryOptions) {
        if (!opt.label) continue;
        const labelLower = opt.label.toLowerCase().trim();
        if (!labelLower || labelLower.length <= bestLen) continue;
        if (!titleLower.endsWith(labelLower)) continue;
        const start = titleLower.length - labelLower.length;
        if (start === 0 || /\s/.test(titleLower[start - 1])) {
          bestValue = opt.value;
          bestLen = labelLower.length;
        }
      }
      return bestValue;
    },
    setForSecurityReturn() {
      // Source = Internal; Group = Lease; Type = Check
      if (!this.selectedDocument) return;
      this.showStickyNote = false;
      const sourceVal = this.findOptionValue(this.sourceOptions, 'Internal');
      const groupVal = this.findOptionValue(this.groupOptions, 'Lease');
      const typeVal = this.findOptionValue(this.typeOptions, 'Check');
      if (sourceVal != null) this.formData.Source = sourceVal;
      this.formData.Group = groupVal != null ? groupVal : 38;
      this.formData.Type = typeVal != null ? typeVal : 8;
      const categoryVal = this.parseCategoryFromTitleEnd(this.formData.Title);
      if (categoryVal != null) {
        this.formData.Category = categoryVal;
      }
      this.saveFormData();
      this.$nextTick(() => {
        const el = this.$refs.docTitle;
        if (el) {
          el.focus();
          el.select();
        }
      });
    },
    viewProperty() {
      // If category is blank and doc type is lease, try to extract from title
      if (!this.formData.Category && this.formData.Type == 10) {
        const title = (this.formData.Title || '').trim();
        if (title && this.categoryOptions.length) {
          // Extract property portion: everything before a date pattern (MMDDYY)
          const dateMatch = title.match(/^(.+?)\s+\d{6}/);
          const propertyPart = dateMatch
            ? dateMatch[1].trim()
            : title.split(/\s+/).slice(0, -1).join(' '); // fallback: drop last token
          if (propertyPart) {
            const propLower = propertyPart.toLowerCase();
            const match = this.categoryOptions.find(
              (opt) => opt.label && opt.label.toLowerCase().includes(propLower),
            );
            if (match) {
              this.formData.Category = match.value;
              this.saveFormData();
            }
          }
        }
      }
      if (!this.formData.Category) return;
      const opt = this.categoryOptions.find(
        (o) => o.value === this.formData.Category,
      );
      if (!opt) return;
      const search = opt.label.replace('#', 'Apt ');
      const resolved = this.$router.resolve({
        path: '/lease-search',
        query: { search, hideNav: '1' },
      });
      this.propertyIframeTitle = opt.label;
      this.propertyIframeLoading = true;
      this.propertyIframeUrl = resolved.href;
    },

    setForLease() {
      // Source = Docusign; Group = Lease; Type = Lease;
      if (this.selectedDocument) {
        this.showStickyNote = false;
        this.formData.Source = 8;
        this.formData.Group = 38;
        this.formData.Type = 10;
        this.formData.Title = '';
        this.saveFormData();
        this.$nextTick(() => {
          const el = this.$refs.docTitle;
          if (el) el.focus();
        });
      }
    },
    prevPage() {
      if (this.currentPage > 0) {
        this.currentPage--;
      }
    },

    nextPage() {
      if (this.hasNextPage) {
        this.currentPage++;
      }
    },

    getSearchFilterFunction(searchQuery, todayStr) {
      let yesterdayStr;
      let idAsNumber;
      switch (searchQuery) {
        case ':o':
          return (doc) => doc.dueBy && !doc.completedOn && doc.dueBy < todayStr;
        case ':t':
          return (doc) => doc.createdOn && doc.createdOn.startsWith(todayStr);
        case ':m':
          return (doc) =>
            doc.modifiedOn &&
            new Date(doc.modifiedOn).toISOString().slice(0, 10) === todayStr;
        case ':dt':
          return (doc) => doc.dueBy && doc.dueBy.startsWith(todayStr);
        case ':y':
          yesterdayStr = new Date(Date.now() - 864e5)
            .toISOString()
            .slice(0, 10);
          return (doc) =>
            doc.createdOn && doc.createdOn.startsWith(yesterdayStr);
        default:
          idAsNumber = parseInt(searchQuery.slice(1));
          if (!isNaN(idAsNumber)) {
            return (doc) => doc.Id === idAsNumber;
          }
          return () => false;
      }
    },

    getTokenFilterFunction(tokens) {
      return (doc) => {
        const docValues = Object.entries(doc).map(([key, value]) => [
          key,
          value != null ? value.toString().toLowerCase() : '',
        ]);

        return tokens.every((token) =>
          docValues.some(([, value]) => value.includes(token)),
        );
      };
    },

    runDownload(event) {
      event.stopPropagation();
      this.openToolsPage(
        12,
        '--skipIntegrity ' + (this.selectedType == 'leases' ? '--leases' : ''),
      );
      return;
    },

    async handleToolsPageClose() {
      // Handle the closing of the ToolsPage component
      this.showToolsPage = false;
      // after download, reset the notification dot counts
      if (this.selectedType == 'leases') {
        this.unreadLeases = 0;
      }
      if (this.selectedType == 'documents') {
        this.unreadDocuments = 0;
      }

      // Sort by createdOn descending to show newest first
      this.sortKey = 'createdOn';
      this.sortOrder = -1;

      // Fetch documents to get the newly added ones
      await this.fetchDocuments(
        0,
        this.selectedType === 'leases'
          ? 10
          : this.selectedType === 'both'
            ? '-'
            : 0,
      );

      // Select the most recently added document (first in sorted list)
      this.selectedDocument = this.sortedDocs[0];
      this.selectDocument(this.selectedDocument);
      this.fetchUnreadCounts();
    },

    openToolsPage(toolId, options) {
      // Open ToolsPage with the given toolId and options
      this.selectedRunTool = {
        toolId: toolId,
        options: options,
      };
      this.showToolsPage = true;
    },

    initToolsPageDrag() {
      if (this.toolsPageDragCleanup) {
        this.toolsPageDragCleanup();
        this.toolsPageDragCleanup = null;
      }
      const el = this.$refs.toolsPageWrapper;
      if (!el) return;
      this.toolsPageDragCleanup = makeDraggable(el, {
        handleSelector: '.tools-page-drag-handle',
        storageKeyPrefix: 'documentScanToolsPage',
      });
    },

    usePattern(pattern) {
      this.searchQuery = pattern;
      this.searchHelp = false;
    },

    onFocus() {
      // Show suggestions when the text area gains focus
      // Position the suggestion box 100px above the textarea
      const textarea = this.$refs.docTitle;
      const rect = textarea.getBoundingClientRect();
      this.suggestionBoxPosition = rect.top - 300;
      if (this.suggestedDocs && this.suggestedDocs.length > 1)
        this.showSuggestions = true;
      else this.showSuggestions = false;
    },

    onBlur() {
      // Hide suggestions after 3 seconds when the text area loses focus
      setTimeout(() => {
        this.showSuggestions = false;
      }, 9000);
    },

    handleNoteDoubleClick() {
      this.noteWasDoubleClicked = true;
      this.isNoteHighlighted = true; // Highlight the note
      this.showOverlay = true; // Enable moving the note
    },

    // Handle the click event on the overlay to reposition the note
    handleOverlayClick(event) {
      const pdfViewer = this.$refs.pdfViewer;

      // Calculate the new position relative to the pdfViewer's top-left corner
      const rect = pdfViewer.getBoundingClientRect(); // Get the position of the PDF viewer
      const newX = event.clientX - rect.left; // Relative X position within the pdfViewer
      const newY = event.clientY - rect.top; // Relative Y position within the pdfViewer

      // Set the new note position relative to the pdfViewer
      this.notePosition = {
        top: newY,
        left: newX,
      };

      // Hide the overlay after repositioning
      this.showOverlay = false;
      this.isNoteHighlighted = false;

      // Save the new position in the database (optional)
      this.saveNotePositionToDatabase(newX, newY);
    },

    async saveNotePositionToDatabase(x, y) {
      const notePosJson = JSON.stringify({ x, y });

      // Assume we are using axios or a similar HTTP client
      try {
        await axios.post('/api/updateNotePosition', {
          documentId: this.selectedDocument.Id,
          notePos: notePosJson,
        });
        // console.log('saveFormData in handleOverlayClick ');
        this.saveFormData();
      } catch (err) {
        console.error('Error updating note position:', err);
      }
    },

    async updateNotePositionDelayed() {
      this.showStickyNote = false;
      setTimeout(() => {
        this.updateNotePosition();
      }, 300); // 200ms delay to allow the PDF viewer to load/render
    },

    // Update the note's position on load or document change (with boundary check)
    async updateNotePosition() {
      const pdfViewer = this.$refs.pdfViewer;

      if (pdfViewer) {
        this.$nextTick(() => {
          const rect = pdfViewer.getBoundingClientRect();

          // Check if the document has a saved note position (notePos)
          if (this.selectedDocument && this.selectedDocument.notePos) {
            const notePos = JSON.parse(this.selectedDocument.notePos);
            let newX = notePos.x;
            let newY = notePos.y;

            const noteWidth = 255; // Width of the sticky note
            const noteHeight = 170; // Height of the sticky note

            // Ensure the note is within the boundaries of the pdfViewer
            if (newX + noteWidth > rect.width) {
              newX = rect.width - noteWidth;
            }
            if (newX < 0) {
              newX = 0;
            }
            if (newY + noteHeight > rect.height) {
              newY = rect.height - noteHeight;
            }
            if (newY < 0) {
              newY = 0;
            }

            this.notePosition = {
              top: newY,
              left: newX,
            };
          } else {
            // Default position if no notePos is saved, ensure it's within bounds
            this.notePosition = {
              top: Math.min(rect.top + 50, rect.height - 200), // Ensure it fits within the height
              left: Math.min(rect.left + 50, rect.width - 350), // Ensure it fits within the width
            };
          }

          // console.log('saveFormData in updateNotePosition');
          if (this.noteWasDoubleClicked) {
            this.saveFormData();
            this.noteWasDoubleClicked = false;
          }
          if (!this.hideStickyNotesByDefault) {
            this.showStickyNote = true; // Show the sticky note after positioning
          }
        });
      }
    },

    setFocusEdit() {
      this.$refs.docTitle.focus();
      this.$refs.docTitle.select();
      this.isEditing = true;
      this.documentReminders.linkedDoc = this.selectedDocument.Id;
    },

    formatDate(dateInput) {
      // If the input is null/undefined, return early
      if (!dateInput) return '-- -- --';

      let date;

      // If it's a string in ISO 8601 format, parse it as a Date object
      if (typeof dateInput === 'string' && dateInput.includes('T')) {
        date = new Date(dateInput); // ISO format
      }
      // If it's a string in YYYY-MM-DD format, manually parse it to avoid timezone issues
      else if (typeof dateInput === 'string' && dateInput.includes('-')) {
        const [year, month, day] = dateInput.split('-');
        date = new Date(year, month - 1, day); // Create a new Date without the timezone shift
      }
      // If it's already a Date object, use it as is
      else {
        date = dateInput;
      }

      // Check if the date is valid
      if (isNaN(date.getTime())) {
        return 'Invalid Date';
      }

      // Extract the local date components without any timezone adjustments
      const year = String(date.getFullYear()).slice(-2); // Get the last two digits of the year
      const month = String(date.getMonth() + 1).padStart(2, '0'); // Get the month and pad with 0
      const day = String(date.getDate()).padStart(2, '0'); // Get the day and pad with 0

      // Return the formatted date as MM/DD/YYYY
      const formatted = `${month}/${day}/${year}`;

      return formatted;
    },

    handleKeyup(event) {
      const selectedValue = event.target.value;

      // If the user selected 'New Category', 'New Group', etc., show the new option modal
      if (selectedValue === 'new') {
        // Parse the label (e.g., "New Category..." => "Category")
        const label = event.target.options[event.target.selectedIndex].text;
        const optionType = label.replace('New ', '').replace('...', ''); // Extract "Category", "Group", etc.

        this.handleNewOption(optionType); // Show modal to add new option
        return; // Exit early to prevent saving until the new option is added
      }

      // Existing keyup functionality (saveFormData, suggestions, etc.)
      clearTimeout(this.saveTimeout);
      clearTimeout(this.typingTimeout);

      this.saveTimeout = setTimeout(() => {
        this.saveFormData(); // Save after 300ms of inactivity
      }, 1000);

      if (
        this.suggestedDocs &&
        this.suggestedDocs.length > 1 &&
        document.activeElement === this.$refs.docTitle
      ) {
        this.showSuggestions = true;
      }

      this.typingTimeout = setTimeout(() => {
        this.showSuggestions = false;
      }, 5000);

      // save the changes
      this.selectedDocument = {
        ...this.selectedDocument, // Keep the existing fields
        ...this.formData, // Overwrite with updated form data
        completedOn: this.documentReminders.completedOn,
      };

      let docIndex = this.importedDocs.findIndex(
        (doc) => doc.Id === this.selectedDocument.Id,
      );

      if (docIndex !== -1) {
        this.importedDocs[docIndex] = {
          ...this.importedDocs[docIndex], // Retain the current values
          ...this.formData, // Update only the new fields
          completedOn: this.documentReminders.completedOn,
          Id: this.importedDocs[docIndex].Id, // Ensure the Id remains intact
        };
      }
    },

    handleKeyDown(event) {
      // Check for Caps Lock
      if (event.getModifierState && event.getModifierState('CapsLock')) {
        this.showDialog('⚠️ Caps Lock is ON', ['OK']);
      }

      // Helper function to check if an input field has focus
      const isInputFocused = () => {
        const activeElement = document.activeElement;
        return (
          activeElement &&
          (activeElement.tagName === 'INPUT' ||
            activeElement.tagName === 'TEXTAREA' ||
            activeElement.contentEditable === 'true')
        );
      };

      // Shift+I: open document import (same as Download button) when mouse over list
      if (
        !isInputFocused() &&
        this.mouseOverTable &&
        event.shiftKey &&
        (event.key === 'I' || event.key === 'i')
      ) {
        event.preventDefault();
        this.runDownload(event);
        return;
      }

      // Look for delete key with a selected document, call deleteDocument(this.selectedDocument)
      if (
        !isInputFocused() &&
        this.mouseOverTable &&
        (event.key === 'Delete' || event.key === 'Backspace') // Check for Delete or Backspace
      ) {
        this.deleteDocument(this.selectedDocument);
        return;
      }

      // toggle sort state
      if (
        !isInputFocused() &&
        this.mouseOverTable &&
        !event.altKey &&
        event.key === 's'
      ) {
        if (this.sortKey === 'dueBy') {
          this.sortKey = 'createdOn';
        } else {
          this.sortKey = 'dueBy';
        }
        return;
      }

      // hide done reminders/documents
      if (!isInputFocused() && this.mouseOverTable && event.key === 'h') {
        this.hideDone = !this.hideDone;
        return;
      }

      // show due today
      if (!isInputFocused() && this.mouseOverTable && event.key === 't') {
        this.searchQuery = ':dt';
        return;
      }
      if (
        !isInputFocused() &&
        this.mouseOverTable &&
        !event.altKey &&
        event.key === 'r'
      ) {
        event.preventDefault();
        this.receivePayment();
        return;
      }

      if (
        !isInputFocused() &&
        this.mouseOverTable &&
        !event.altKey &&
        (event.key === 'c' || event.key === 'C')
      ) {
        event.preventDefault();
        this.setForReceiveCheck();
        return;
      }

      // handle 'd' key for dueBy date or completedOn
      if (!isInputFocused() && this.mouseOverTable && event.key === 'd') {
        this.hideDone = false;
        const today = new Date().toISOString().split('T')[0]; // Get today's date in YYYY-MM-DD format
        if (!this.documentReminders.dueBy) {
          this.documentReminders.dueBy = today; // Set dueBy to today
        }
        if (this.documentReminders.completedOn) {
          this.documentReminders.completedOn = null; // Toggle completedOn to null
        } else {
          this.documentReminders.completedOn = today; // Set completedOn to today
        }

        const stringifiedNotePos = JSON.stringify({
          x: this.notePosition.left, // Convert 'left' to 'x'
          y: this.notePosition.top, // Convert 'top' to 'y'
        });

        this.selectedDocument = {
          ...this.selectedDocument, // Keep existing fields
          ...this.formData, // Overwrite with updated form data
          notePos: stringifiedNotePos, // Assign stringified notePos
          completedOn: this.documentReminders.completedOn,
          dueBy: this.documentReminders.dueBy,
        };

        let docIndex = this.importedDocs.findIndex(
          (doc) => doc.Id === this.selectedDocument.Id,
        );

        if (docIndex !== -1) {
          this.importedDocs[docIndex] = {
            ...this.importedDocs[docIndex], // Spread the existing properties
            completedOn: this.documentReminders.completedOn,
            dueBy: this.documentReminders.dueBy,
          };

          // Store the next document to select AFTER the save completes
          let filteredDocIndex = this.filteredDocs.findIndex(
            (doc) => doc.Id === this.selectedDocument.Id,
          );
          this.selectedDocumentAfterSave =
            this.filteredDocs[filteredDocIndex + 1];
        }
        // Note: The next document will be selected in saveReminder() after the save completes
        return;
      }

      // Handle 'Escape' key to reset search query
      if (event.key === 'Escape' && !isInputFocused()) {
        this.searchQuery = '';
        this.selectedRowIndex = null;
        this.selectedDocument = null;
        this.$refs.searchQuery.focus();
        return;
      }

      // Handle 'Alt + s' key to set search query to the first two tokens of Title
      if (this.selectedDocument && event.altKey && event.key === 's') {
        const tokens = this.selectedDocument.Title.split(' ');
        const firstTwoTokens = tokens.slice(0, 2);
        this.searchQuery = firstTwoTokens.join(', ');
        return;
      }

      // Handle 'Tab' key to select the first search result
      if (
        event.key === 'Tab' &&
        this.searchResults.length > 0 &&
        this.$refs.paymentSearch === document.activeElement // Check if the input has focus
      ) {
        event.preventDefault(); // Prevent the default tab behavior
        this.selectResult(this.filteredPeopleProperties[0]); // Select the first result
        return;
      }

      // Handle 'ArrowUp' and 'ArrowDown' key navigation through documents
      if (
        !isInputFocused() &&
        this.mouseOverTable &&
        (event.key === 'ArrowDown' || event.key === 'ArrowUp')
      ) {
        // Prevent default scrolling behavior for arrow keys
        event.preventDefault();
      }

      if (!isInputFocused() && this.mouseOverTable) {
        if (this.sortedDocs.length === 0) return; // No rows to select

        if (event.ctrlKey && event.key === 'ArrowUp') {
          this.selectedRowIndex = 0;
        } else if (event.key === 'ArrowDown') {
          if (this.selectedRowIndex === null) {
            this.selectedRowIndex = 0; // Select the first row if none selected
          } else if (this.selectedRowIndex < this.sortedDocs.length - 1) {
            this.selectedRowIndex++;
          }
        } else if (event.key === 'ArrowUp') {
          if (this.selectedRowIndex > 0) {
            this.selectedRowIndex--;
          }
        }

        if (event.key === 'ArrowUp' || event.key === 'ArrowDown') {
          this.selectedDocument = this.sortedDocs[this.selectedRowIndex];
          this.selectDocument(this.selectedDocument);

          // Scroll the selected row into view
          this.$nextTick(() => {
            const selectedRow = this.$refs[`row-${this.selectedRowIndex}`];
            if (selectedRow && selectedRow[0]) {
              selectedRow[0].scrollIntoView({
                behavior: 'smooth', // Optional smooth scroll
                block: 'nearest', // Scrolls the row into view
              });
            }
          });
        }
      }
    },

    setType() {
      localStorage.setItem('docType', this.selectedType);
      this.formData = {};
      this.fetchDocuments(
        0,
        this.selectedType === 'leases'
          ? 10
          : this.selectedType === 'both'
            ? '-'
            : 0,
      );
    },

    // Fetch documents from the API (with pagination support, starting with page 1)
    async fetchDocuments(page = 0, type) {
      try {
        const response = await axios.get(
          `/api/scanned-documents/${page}/${type}`,
        );
        this.importedDocs = response.data; // Assuming the API returns an array of documents
        return response.data;
      } catch (error) {
        console.error('Error fetching documents:', error);
        this.updateFooterMessage('Failed to load documents.');
        throw error;
      }
    },

    // Clear the search input
    clearSearch() {
      this.searchQuery = '';
      this.autoSelected = false;
      this.expandedSearch = false;
    },

    // Select a document and fetch its PDF using the actual API
    onPdfFrameLoad() {
      this.pdfLoading = false;
    },

    async selectDocument(doc) {
      this.isEditing = false;
      this.isSearchFocused = false;
      this.selectedDocument = doc;
      this.fetchReminders(doc.Id);
      this.updateNotePositionDelayed();
      this.documentId = doc.Id;
      this.selectedRowIndex = this.sortedDocs.findIndex((d) => d.Id === doc.Id);
      localStorage.setItem('selectedDocument', doc.Id);
      if (this.pdfUrl && String(this.pdfUrl).startsWith('blob:')) {
        URL.revokeObjectURL(this.pdfUrl);
      }
      this.pdfUrl = null;
      this.pdfLoading = true;

      // Populate form data
      this.formData = {
        Title: doc.Title || '',
        Description: doc.Description || '',
        Category: doc.Category || null,
        Group: doc.Group || null,
        Source: doc.Source || null,
        Type: doc.Type || null,
        Note: doc.Note || '',
      };

      try {
        // Fetch the PDF directly from the backend
        const response = await axios.get(`/api/load-pdf/${doc.Id}`, {
          responseType: 'arraybuffer',
          timeout: 35000,
        });

        if (response.status === 200) {
          // Create a Blob from the response data
          const pdfBlob = new Blob([response.data], {
            type: 'application/pdf',
          });
          const pdfUrl = URL.createObjectURL(pdfBlob);

          // Set the URL for your iframe or download link
          this.pdfUrl = pdfUrl;
          this.updateFooterMessage(`PDF for document ID ${doc.Id} loaded.`);
        } else {
          this.pdfLoading = false;
          console.error(
            `Failed to load PDF for document ID ${doc.Id}. Status: ${response.status}`,
          );
          this.updateFooterMessage('Failed to load PDF: Server error.');
        }
      } catch (error) {
        this.pdfLoading = false;
        console.error('Error loading PDF:', error);
        const timedOut =
          error.code === 'ECONNABORTED' || error.response?.status === 504;
        this.updateFooterMessage(
          timedOut
            ? 'PDF load timed out — document storage may be unavailable.'
            : 'Failed to load PDF.',
        );
      }
    },

    // Function to update footer messages
    updateFooterMessage(message) {
      this.footerMessage = message;
    },

    // Load dropdown options from the API
    async loadDropdownOptions() {
      try {
        const response = await axios.get('/api/optionsValues');
        const options = response.data || {}; // Default to an empty object if response is empty

        // Helper function to capitalize the first letter of each word
        const capitalizeFirstLetter = (str) => {
          return str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();
        };

        // Function to process the options: filter, capitalize, and sort
        const processOptions = (optionArray) => {
          return (optionArray || [])
            .filter((option) => !option.label.includes('-')) // Remove any options with '-'
            .map((option) => ({
              label: capitalizeFirstLetter(option.label), // Capitalize the first letter
              value: option.value,
            }))
            .sort((a, b) => a.label.localeCompare(b.label)); // Sort alphabetically by label
        };

        // Access and process the arrays directly from the options object
        this.categoryOptions = processOptions(options.Category);
        this.groupOptions = processOptions(options.Group);
        this.sourceOptions = processOptions(options.Source);
        this.typeOptions = processOptions(options.Type);
      } catch (error) {
        console.error('Error fetching options:', error);
      }
    },

    // Handle row click to prefill form data
    handleRowClick(document) {
      this.formData = { ...document }; // Assuming document contains fields like Title, Description, etc.
      this.documentId = document.Id;
    },

    // Handle saving data after mouseover or timeout
    handleMouseover() {
      return;
    },

    handleMouseleave() {
      this.isEditing = false;
    },

    handleTableMouseover(event) {
      this.mouseOverTable = true;
      console.log('mouse over table');

      // Clear focus from any inputs in document-form or reminder-form
      const activeElement = document.activeElement;
      if (
        activeElement &&
        (activeElement.tagName === 'INPUT' ||
          activeElement.tagName === 'TEXTAREA')
      ) {
        const formElement = activeElement.closest(
          '.document-form, .reminder-form',
        );
        if (formElement) {
          activeElement.blur();
        }
      }

      event.preventDefault();
    },

    handleTableMouseleave() {
      this.mouseOverTable = false;
      console.log('mouse NOT over table');
    },

    // on loss of focus from Notes textarea, focus back to title
    handleNotesBlur() {
      if (this.isEditing) this.setFocusEdit();
    },

    // Save form data to the database
    async saveFormData() {
      this.footerMessage = 'saving data';

      if (this.selectedDocument) {
        try {
          // Step 1: Fetch the modifiedOn and modifiedBy for the selected document
          const response = await axios.get(
            `/api/documents/${this.selectedDocument.Id}/modified`,
          );

          const { modifiedBy, modifiedOn } = response.data;
          const wasModified = new Date(modifiedOn).getTime();
          const rightNow = new Date(this.selectedDocument.modifiedOn).getTime();
          const modifiedUser = this.users.find(
            (user) => user.id === modifiedBy,
          );
          // Step 2: Compare the modifiedOn from the database with the local one
          // and whether it was modified by self, or not
          if (
            this.enableOverwriteChecking &&
            wasModified !== rightNow &&
            modifiedBy != this.myUserId
          ) {
            // Step 3: Show confirmation dialog if document was modified
            const confirmationMessage = `This document was updated by ${modifiedUser ? modifiedUser.displayName : 'another user'}. \n\nDo you want to overwrite those changes? (if not, the document table will be refreshed with the other changes, and you can try your edits again.)`;
            const result = await this.showDialog(confirmationMessage, [
              'Overwrite',
              'Cancel',
            ]);

            if (result === 'Cancel') {
              this.footerMessage = 'Save cancelled: refreshing data';
              await this.fetchDocuments(
                0,
                this.selectedType === 'leases'
                  ? 10
                  : this.selectedType === 'both'
                    ? '-'
                    : 0,
              );
              //find the same row again after refreshing the data
              this.selectedRowIndex = this.sortedDocs.findIndex(
                (d) => d.Id === this.selectedDocument.Id,
              );
              //and now, select that one.
              this.selectDocument(this.sortedDocs[this.selectedRowIndex]);
              return;
            }
          }

          // Step 4: Proceed to save if no conflict or user confirms overwrite
          const saveResponse = await axios.post(
            `/api/documents/${this.selectedDocument.Id}`,
            this.formData,
          );

          // Update the document in the local state after saving
          // Use the actual modifiedOn timestamp from the server to avoid precision mismatch
          this.selectedDocument = {
            ...this.selectedDocument,
            ...this.formData,
            modifiedOn: saveResponse.data.document.modifiedOn,
            modifiedBy: saveResponse.data.document.modifiedBy,
          };

          this.footerMessage = 'Document saved';
        } catch (error) {
          console.error('Error saving document:', error);
          this.footerMessage = 'Error saving document';
        }
      }
    },

    // Open the modal to add a new option
    handleNewOption(type) {
      this.newOptionType = type;
      this.addingNewOption = true; // Set flag to true to block saving
      this.showNewOptionModal = true;
    },

    // Add the new option to the options table
    async addNewOption() {
      try {
        // Save the new option directly via API (no need for maxValue call)
        await axios.post('/api/add-option', {
          option: this.newOptionType,
          label: this.newOptionLabel,
        });

        // Refresh dropdown options
        await this.loadDropdownOptions();

        // Set the new option in the form data and reset states
        this.formData[this.newOptionType] = this.newOptionLabel; // Assuming you're setting the label or value here
        this.addingNewOption = false; // Reset flag
        this.showNewOptionModal = false;
      } catch (error) {
        console.error('Error adding new option:', error);
      }
    },

    // Close the modal without adding
    closeNewOptionModal() {
      this.showNewOptionModal = false;
    },

    // Delete document handler (if needed)
    async deleteDocument() {
      try {
        // Show dialog and wait for confirmation
        const result = await this.showDialog(
          'Are you sure you want to delete this document?',
          ['Confirm', 'Cancel'],
        );

        // If confirmed, proceed with the deletion
        if (result === 'Confirm') {
          // Proceed with deletion if confirmed
          await axios.delete(`/api/documents/${this.documentId}`);

          // Clear the form after deletion
          this.formData = {
            Title: '',
            Description: '',
            Category: null,
            Group: null,
            Source: null,
            Type: null,
            Note: '',
          };

          // Remove the document from the importedDocs array
          const docIndex = this.importedDocs.findIndex(
            (doc) => doc.Id === this.documentId,
          );
          if (docIndex !== -1) {
            this.importedDocs.splice(docIndex, 1); // Remove the document from the array
          }

          // Clear the documentId and pdfUrl
          this.documentId = null;
          this.pdfUrl = null;
          this.pdfLoading = false;
        }
      } catch (error) {
        // Handle errors properly
        console.error('Error deleting document:', error);
      }
    },

    // share document
    shareDocument() {
      // Construct the URL with the search query containing the document ID
      if (!this.selectedDocument) return;

      const docID = this.selectedDocument.Id;
      const url = `${window.location.origin}${window.location.pathname}?search=:${docID}`;
      // console.log(docID, url);

      // Copy the URL to the clipboard
      if (navigator.clipboard) {
        navigator.clipboard
          .writeText(url)
          .then(() => {
            this.footerMessage =
              'shareable Document URL copied to clipboard. Paste it where needed.';
          })
          .catch(() => {
            this.footerMessage =
              'Failed to copy Document URL copied to clipboard';
          });
      } else {
        this.footerMessage = 'use this url: ' + url;
      }
    },

    openDocumentEmailPopup() {
      const selectedId = this.selectedDocument?.Id;
      const rowIsCurrentlySelected = !!(
        selectedId && this.filteredDocs.some((doc) => doc.Id === selectedId)
      );

      if (!rowIsCurrentlySelected) {
        this.docEmailError = 'please select one document first.';
        this.showDocEmailPopup = true;
        return;
      }

      this.docEmailError = '';
      this.docEmailTo = '';
      this.docEmailSubject = this.formData?.Title
        ? `Document: ${this.formData.Title}`
        : `Document ID ${this.selectedDocument.Id}`;
      this.docEmailMessage = 'Please see attached document.';
      this.docEmailPropertyMode = false;
      this.docEmailPropertyMatches = [];
      this.showDocEmailPopup = true;
    },

    closeDocumentEmailPopup() {
      this.showDocEmailPopup = false;
      this.sendingDocEmail = false;
      this.docEmailError = '';
      this.docEmailPropertyMode = false;
      this.docEmailPropertyMatches = [];
    },

    toggleDocEmailPropertyMode() {
      this.docEmailPropertyMode = !this.docEmailPropertyMode;
      this.docEmailPropertyMatches = [];
      if (this.docEmailPropertyMode) {
        this.onDocEmailToInput();
      }
    },

    async onDocEmailToInput() {
      if (!this.docEmailPropertyMode) {
        return;
      }

      const query = (this.docEmailTo || '').trim();
      const firstToken = query.split(',')[0].trim();
      if (!firstToken) {
        this.docEmailPropertyMatches = [];
        return;
      }

      try {
        const response = await axios.get('/api/properties', {
          params: { query: firstToken },
        });
        const allMatches = response.data || [];
        const terms = query
          .toLowerCase()
          .split(',')
          .map((term) => term.trim())
          .filter(Boolean);

        const filtered = allMatches
          .filter((row) =>
            terms.every((term) =>
              (row.display || '').toLowerCase().includes(term),
            ),
          )
          .slice(0, 5);

        this.docEmailPropertyMatches = filtered;

        if (filtered.length === 1) {
          await this.selectDocEmailProperty(filtered[0]);
        }
      } catch (error) {
        console.error(
          'Error searching properties for email recipients:',
          error,
        );
        this.docEmailPropertyMatches = [];
      }
    },

    normalizePropertyAddress(value) {
      return String(value || '')
        .toLowerCase()
        .replace(/\bapt\b/g, '')
        .replace(/\bunit\b/g, '')
        .replace(/#/g, '')
        .replace(/[^a-z0-9]/g, '');
    },

    async ensureTenantPropertyEmailsLoaded() {
      if (this.tenantPropertyEmailRows.length > 0) {
        return;
      }
      const response = await axios.get('/api/tenant-property-emails');
      this.tenantPropertyEmailRows = response.data || [];
    },

    async selectDocEmailProperty(property) {
      if (!property) {
        return;
      }

      try {
        await this.ensureTenantPropertyEmailsLoaded();
        const normalizedTarget = this.normalizePropertyAddress(
          property.display,
        );
        const matchingRows = this.tenantPropertyEmailRows.filter((row) => {
          const normalizedRow = this.normalizePropertyAddress(row.address);
          return (
            normalizedRow === normalizedTarget ||
            normalizedRow.includes(normalizedTarget) ||
            normalizedTarget.includes(normalizedRow)
          );
        });

        const emailSet = new Set();
        matchingRows.forEach((row) => {
          String(row.email || '')
            .split(';')
            .map((email) => email.trim())
            .filter(Boolean)
            .forEach((email) => emailSet.add(email));
        });

        this.docEmailTo = Array.from(emailSet).join('; ');
        this.docEmailPropertyMode = false;
        this.docEmailPropertyMatches = [];

        if (!this.docEmailTo) {
          this.footerMessage = `No tenant emails found for ${property.display}.`;
        }
      } catch (error) {
        console.error('Error selecting property recipients:', error);
        this.footerMessage = 'Could not load tenant emails for that property.';
      }
    },

    highlightDocEmailPropertyMatch(text) {
      if (!text) {
        return '';
      }
      const terms = (this.docEmailTo || '')
        .split(',')
        .map((term) => term.trim())
        .filter(Boolean);

      if (!terms.length) {
        return text;
      }

      let highlighted = text;
      terms.forEach((term) => {
        const escaped = term.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
        const regex = new RegExp(`(${escaped})`, 'gi');
        highlighted = highlighted.replace(
          regex,
          '<span class="highlight">$1</span>',
        );
      });
      return highlighted;
    },

    async sendDocumentEmail() {
      if (!this.docEmailTo || !this.docEmailSubject || !this.docEmailMessage) {
        this.footerMessage =
          'Please provide email address, subject, and message.';
        return;
      }

      if (!this.selectedDocument || !this.selectedDocument.Id) {
        this.footerMessage = 'you must select one document first.';
        return;
      }

      this.sendingDocEmail = true;

      try {
        await axios.post('/api/send-document-email', {
          docId: this.selectedDocument.Id,
          to: this.docEmailTo,
          subject: this.docEmailSubject,
          message: this.docEmailMessage,
        });

        this.footerMessage = 'Document email sent.';
        this.closeDocumentEmailPopup();
      } catch (error) {
        console.error('Failed to send document email:', error);
        this.footerMessage = 'Failed to send document email.';
        this.sendingDocEmail = false;
      }
    },

    async fetchReminders(documentId) {
      try {
        const response = await axios.get(`/api/reminders/${documentId}`);

        const reminder = response.data || {};

        // Format date fields (if they exist) to 'yyyy-MM-dd' format
        const formatDate = (dateStr) =>
          dateStr ? new Date(dateStr).toISOString().split('T')[0] : null;

        this.documentReminders = {
          ...reminder,
          dueBy: formatDate(reminder.dueBy),
          remindOn: formatDate(reminder.remindOn),
          completedOn: formatDate(reminder.completedOn),
        };
      } catch (error) {
        console.error('Error fetching reminders:', error);
      }
    },

    async fetchUsers() {
      try {
        const response = await axios.get('/api/users');
        this.users = response.data || [];
        const currentUser = this.users
          ? this.users.find((user) => user.you === 1)
          : null; // Find the user where 'you' === 1
        // console.log('userdata:', this.users);
        // console.log('Curruser:', currentUser);
        this.myUserId = currentUser.id;
      } catch (error) {
        console.error('Error fetching users:', error);
      }
    },

    async saveReminder() {
      if (this.addingNewOption) {
        // Block saving because we are still adding a new option
        console.warn('Please finish adding the new option before saving.');
        return;
      }
      try {
        // Check if dueBy has a value and remindOn does not
        if (this.documentReminders.dueBy && !this.documentReminders.remindOn) {
          const dueByDate = new Date(this.documentReminders.dueBy);

          // Subtract 7 days from dueBy
          const remindOnDate = new Date(dueByDate);
          remindOnDate.setDate(dueByDate.getDate() - 7);

          // Set remindOn to the new date, formatted in YYYY-MM-DD
          this.documentReminders.remindOn = remindOnDate
            .toISOString()
            .split('T')[0];
        }

        await axios.post('/api/document-reminders', {
          ...this.documentReminders,
          title: this.selectedDocument.Title,
          linkedDoc: this.selectedDocument.Id,
        });

        // After successful save, update both dueBy and completedOn fields in importedDocs
        const docIndex = this.importedDocs.findIndex(
          (doc) => doc.Id === this.selectedDocument.Id,
        );

        if (docIndex !== -1) {
          this.importedDocs[docIndex].dueBy = this.documentReminders.dueBy;
          this.importedDocs[docIndex].completedOn =
            this.documentReminders.completedOn;

          // After the save is complete, select the next row in the table
          if (this.selectedDocumentAfterSave) {
            this.selectDocument(this.selectedDocumentAfterSave);
            this.selectedDocumentAfterSave = null;
          }
        }
      } catch (error) {
        console.error('Error saving reminder:', error);
      }
    },

    createNewReminder() {
      this.documentReminders = {
        linkedDoc: this.selectedDocument.Id,
        dueBy: null,
        remindOn: null,
        priority: 2, // Default to Med
        assignedTo: null,
        completedOn: null,
        completedBy: null,
      };
    },
    // Adds a class to apply the strikethrough if dueBy exists and completedOn is not null
    getDueByClass(doc) {
      return doc.dueBy && doc.completedOn ? 'strikethrough' : '';
    },

    sortBy(column) {
      if (this.sortKey === column) {
        // Toggle sort order if the same column is clicked
        this.sortOrder *= -1;
      } else {
        // Set sort key and default to ascending order
        this.sortKey = column;
        this.sortOrder = 1;
      }
    },

    // Display the sorting indicator (▲ or ▼)
    getSortIndicator(column) {
      if (this.sortKey === column) {
        return this.sortOrder === 1 ? '▲' : '▼';
      }
      return '';
    },

    async hideDoneDocs() {
      this.hideDone = !this.hideDone;
    },

    resolveWho(modifiedBy) {
      const user = this.users.find((user) => user.id === modifiedBy);
      return user ? user.displayName : 'Unknown'; // Return 'Unknown' if no user matches
    },

    async fetchUnreadCounts() {
      try {
        const response = await axios.get('/api/checkUnreadState');
        this.unreadDocuments = response.data.scanDocuments;
        this.unreadLeases = response.data.scanLeases;
      } catch (error) {
        console.error('Failed to fetch unread counts:', error);
        // Handle error appropriately
      }
    },

    showDialog(message, options) {
      this.dialogMessage = message;
      this.dialogOptions = options;
      this.showDialogPopup = true;

      return new Promise((resolve) => {
        this.dialogCallback = resolve;
      });
    },

    onDialogOptionSelected(option) {
      this.showDialogPopup = false;
      if (this.dialogCallback) {
        this.dialogCallback(option);
        this.dialogCallback = null; // Clear the callback
      }
    },
    searchFocus() {
      this.isSearchFocused = true; // Set to true when the search input is focused
    },
    searchBlur() {
      this.isSearchFocused = false; // Set to false when the search input loses focus
    },
    handleSelectDocument(linkedDocId) {
      // Find the document in importedDocs based on linkedDocId
      this.pdfUrl = null;
      this.PDFplaceholderText = '&larr; loading PDF, please wait...';
      const selectedDoc = this.importedDocs.find(
        (doc) => doc.Id === linkedDocId,
      );

      if (selectedDoc) {
        // Pass the document to the selectDocument method
        this.selectDocument(selectedDoc);
      } else {
        console.warn(
          `Document with ID ${linkedDocId} not found in importedDocs.`,
        );
      }
    },
    checkForUpdates() {
      axios
        .get('/api/get-latest-document')
        .then((response) => {
          const latestModifiedOn = response.data.modifiedOn; // Assuming the response contains modifiedOn
          const modifiedBy = response.data.modifiedBy; // Assuming the response contains modifiedBy

          // Compare the latest timestamp with docLastUpdatedOn (and whether this user made the change)
          if (
            !this.docLastUpdatedOn ||
            (new Date(latestModifiedOn) > new Date(this.docLastUpdatedOn) &&
              modifiedBy != this.myUserId)
          ) {
            this.footerMessage =
              'The database was updated by another user. fetching documents...';
            this.docLastUpdatedOn = latestModifiedOn; // Update the last updated timestamp
            this.fetchDocuments(
              0,
              this.selectedType === 'leases'
                ? 10
                : this.selectedType === 'both'
                  ? '-'
                  : 0,
            );
          } else {
            // console.log('No new updates.');
          }
        })
        .catch((error) => {
          console.error('Error checking for document updates:', error);
        });
    },

    async submitPayment(withEmail) {
      // Save current input to payments array first
      this.saveCurrentPayment();

      const ledgerEffectiveDate = this.formatEffectiveDateForApi(
        this.leaseEffectiveDateForResult(this.selectedResult),
      );

      this.isSubmitting = true;
      let confirmationMessage = '';

      // Validate all payments
      for (let i = 0; i < this.payments.length; i++) {
        const payment = this.payments[i];
        const checkNum = i + 1;

        if (
          typeof payment.checkNumber !== 'string' ||
          !payment.checkNumber.trim()
        ) {
          confirmationMessage += `Check ${checkNum}: Check number is invalid.\n`;
        }
        if (
          !payment.checkDate ||
          isNaN(new Date(payment.checkDate).getTime())
        ) {
          confirmationMessage += `Check ${checkNum}: Check date is invalid.\n`;
        }
        const amt =
          typeof payment.amount === 'string'
            ? parseFloat(payment.amount)
            : payment.amount;
        if (isNaN(amt) || amt <= 0) {
          confirmationMessage += `Check ${checkNum}: Amount is invalid.\n`;
        }
      }

      // If there are validation errors, show the dialog
      if (confirmationMessage) {
        confirmationMessage += '\nPlease correct these errors to continue.';
        await this.showDialog(confirmationMessage, ['OK']);
        this.isSubmitting = false;
        return;
      }

      if (withEmail && !this.selectedResult.email) {
        await this.showDialog(
          'No email address exists for this person, cannot send. Use Submit only.',
          ['OK'],
        );
        this.isSubmitting = false;
        return;
      }

      // Build received notices for all payments
      const timestamp = new Date()
        .toLocaleString('en-US', {
          month: '2-digit',
          day: '2-digit',
          year: 'numeric',
          hour: 'numeric',
          minute: '2-digit',
          hour12: true,
        })
        .toLowerCase();

      const receivedNotices = this.payments.map((payment) => {
        const amt =
          typeof payment.amount === 'string'
            ? parseFloat(payment.amount)
            : payment.amount;
        return `received payment${this.isSecurityDeposit ? ' (security deposit)' : ''}: check #${payment.checkNumber} for $${amt.toFixed(2)} on ${timestamp}`;
      });

      // Update note with all payment notices
      const allNotices = receivedNotices.join('\n');
      if (this.formData.Note) {
        this.formData.Note = allNotices + '\n' + this.formData.Note;
      } else {
        this.formData.Note = allNotices;
      }

      // Update the importedDocs array with the updated Note field
      const docIndex = this.importedDocs.findIndex(
        (doc) => doc.Id === this.selectedDocument.Id,
      );
      if (docIndex !== -1) {
        this.importedDocs[docIndex].Note = this.formData.Note;
      }
      this.saveFormData();

      try {
        // Submit all payments to the API
        for (const payment of this.payments) {
          const amt =
            typeof payment.amount === 'string'
              ? parseFloat(payment.amount)
              : payment.amount;
          await axios.post('/api/upsert-lease-payment', {
            docId: this.selectedDocument.Id,
            address: this.selectedResult.address,
            fullName: this.selectedResult.fullName,
            leaseId: this.selectedResult.leaseId,
            peopleId: this.selectedResult.peopleId,
            email: this.selectedResult.email,
            checkNumber: payment.checkNumber,
            checkDate: payment.checkDate,
            amount: amt,
            paymentMethod: payment.paymentMethod,
            purpose: this.isSecurityDeposit ? 4 : 1,
            status: parseInt(payment.paymentStatus) || 0,
            effectiveDate: ledgerEffectiveDate,
            withEmail: false, // Don't send individual emails
          });
        }

        await axios.post('/api/send-combined-payment-email', {
          email: this.selectedResult.email,
          fullName: this.selectedResult.fullName,
          address: this.selectedResult.address,
          peopleId: this.selectedResult.peopleId,
          propertyId: this.selectedResult.propertyId,
          leaseId: this.selectedResult.leaseId,
          sendEmail: Boolean(withEmail && this.selectedResult.email),
          payments: this.payments.map((p) => ({
            checkNumber: p.checkNumber,
            checkDate: p.checkDate,
            amount:
              typeof p.amount === 'string' ? parseFloat(p.amount) : p.amount,
          })),
          isSecurityDeposit: this.isSecurityDeposit,
        });

        this.isSubmitting = false;
        this.closeModal();

        // Find the index of the selected document in sortedDocs
        const index = this.sortedDocs.indexOf(this.selectedDocument);

        // Check if the next index is within bounds of the array
        if (index !== -1 && index < this.sortedDocs.length - 1) {
          // Set selectedDocument to the next item
          this.selectedDocument = this.sortedDocs[index + 1];
          this.selectDocument(this.selectedDocument);
        }
      } catch (error) {
        console.error('Error submitting payment:', error);
        this.isSubmitting = false;
        await this.showDialog('Error submitting payment. Please try again.', [
          'OK',
        ]);
      }
    },

    async receivePayment() {
      this.isSearchFocused = true;
      if (
        !(
          this.selectedDocument &&
          (this.selectedDocument.typeLabel === 'check' ||
            this.selectedDocument.Type == 8)
        )
      ) {
        const confirmationMessage =
          'To receive a payment, please select a check document first.';
        const result = await this.showDialog(confirmationMessage, ['OK']);

        if (result === 'OK') {
          return;
        }
      }

      // Parse title and description to pre-fill fields
      this._parsedCheckNumber = '';
      this._parsedAmount = '';
      this._parsedLastName = '';

      const title = (this.formData.Title || '').trim();
      const description = (this.formData.Description || '').trim();

      // Parse title: "Rent check for April 2026 600 Michigan #1 Kuhn"
      // or "Security Deposit check for 216 John 3R Martinez"
      // After the prefix comes address and optional last name
      const rentTitleMatch = title.match(
        /^Rent check for \w+ \d{2,4}\s+(.+)$/i,
      );
      const secDepTitleMatch = title.match(
        /^Security Deposit check for\s+(.+)$/i,
      );
      const titleMatch = rentTitleMatch || secDepTitleMatch;
      if (titleMatch) {
        const afterPrefix = titleMatch[1].trim();
        // Split into tokens; last token is the last name if it doesn't look like
        // part of an address (no # prefix, not a number)
        const tokens = afterPrefix.split(/\s+/);
        const lastToken = tokens[tokens.length - 1];
        if (
          tokens.length > 1 &&
          !/^#/.test(lastToken) &&
          !/^\d+$/.test(lastToken)
        ) {
          this._parsedLastName = lastToken;
        }
        // Address is everything (used for category matching)
        const addressPart = this._parsedLastName
          ? tokens.slice(0, -1).join(' ')
          : afterPrefix;

        // Try to match category by address
        if (addressPart && this.categoryOptions.length) {
          const addrLower = addressPart.toLowerCase();
          const match = this.categoryOptions.find(
            (opt) => opt.label && opt.label.toLowerCase().includes(addrLower),
          );
          if (match) {
            this.formData.Category = match.value;
            this.saveFormData();
          }
        }
      }

      // Parse description for check numbers and amounts.
      // Supports multiple checks on separate lines, e.g.:
      //   #19-718787534 $412
      //   #20-123456789 $650
      // Tokens can appear in either order on each line.
      this._parsedPayments = this.parseCheckPaymentsFromDescription(description);
      if (this._parsedPayments.length > 0) {
        this._parsedCheckNumber = this._parsedPayments[0].checkNumber;
        this._parsedAmount = this._parsedPayments[0].amount;
      }

      this.isSecurityDeposit = isSecurityDepositCheckTitle(title);
      this.showReceivePayment = true;

      // Pre-fill search with last name if parsed
      if (this._parsedLastName) {
        this.searchTerm = this._parsedLastName;
        this.searchPeopleProperties();
      }

      this.$nextTick(() => {
        this.$refs.paymentSearch.focus();
      });
    },

    searchPeopleProperties({ force = false } = {}) {
      const mode = this.includePreviousTenants ? 'previous' : 'current';
      if (
        !force &&
        this.searchResults.length > 0 &&
        this.searchResultsMode === mode
      ) {
        return;
      }

      axios
        .get('/api/people-properties', {
          params: {
            includePrevious: this.includePreviousTenants ? '1' : '0',
          },
        })
        .then((response) => {
          this.searchResults = response.data;
          this.searchResultsMode = mode;
        })
        .catch((error) => {
          console.error('Error fetching search results:', error);
        });
    },

    togglePreviousTenants() {
      this.includePreviousTenants = !this.includePreviousTenants;
      this.selectedResult = null;
      this.searchResults = [];
      this.searchResultsMode = null;
      this.searchPeopleProperties({ force: true });
    },

    formatLeaseDate(dateValue) {
      if (!dateValue) {
        return '?';
      }
      const date = new Date(dateValue);
      if (Number.isNaN(date.getTime())) {
        return '?';
      }
      return date.toLocaleDateString('en-US', {
        month: '2-digit',
        day: '2-digit',
        year: '2-digit',
        timeZone: 'America/New_York',
      });
    },

    dateInputFromValue(dateValue) {
      if (!dateValue) {
        return '';
      }
      const raw = String(dateValue);
      const isoMatch = raw.match(/^(\d{4}-\d{2}-\d{2})/);
      if (isoMatch) {
        return isoMatch[1];
      }
      const date = new Date(dateValue);
      if (Number.isNaN(date.getTime())) {
        return '';
      }
      return date.toISOString().slice(0, 10);
    },

    isFutureLeaseResult(result) {
      if (!result) {
        return false;
      }
      if (Number(result.isFutureLease) === 1) {
        return true;
      }
      if (!result.startsOn) {
        return false;
      }
      const todayStr = new Date().toISOString().slice(0, 10);
      return this.dateInputFromValue(result.startsOn) > todayStr;
    },

    leaseEffectiveDateForResult(result) {
      if (!this.isFutureLeaseResult(result)) {
        return '';
      }
      return this.dateInputFromValue(result.startsOn);
    },

    formatEffectiveDateForApi(dateValue) {
      const dateInput = this.dateInputFromValue(dateValue);
      if (!dateInput) {
        return null;
      }
      return `${dateInput} 00:00:00`;
    },

    formatReceivePaymentResult(result) {
      const base = `${result.fullName}, ${result.address}, ${result.email}`;
      if (this.includePreviousTenants) {
        const start = this.formatLeaseDate(result.startsOn);
        const end = this.formatLeaseDate(result.endsOn);
        return `${base} (${start} - ${end})`;
      }
      if (this.isFutureLeaseResult(result)) {
        const start = this.formatLeaseDate(result.startsOn);
        return `${base} (lease starts ${start})`;
      }
      return `${base} (${result.endsOn ? 'current lease)' : 'old lease)'}`;
    },

    selectResult(result) {
      // When a user clicks on a search result, save the selected result and show the input fields
      this.selectedResult = result;
      // Initialize payments array from parsed description (supports multiple checks)
      if (this._parsedPayments && this._parsedPayments.length > 0) {
        this.payments = this._parsedPayments.map((p) => ({
          checkNumber: p.checkNumber || '',
          checkDate: '',
          amount: p.amount || '',
          paymentStatus: '0',
          paymentMethod: '1',
        }));
      } else {
        this.payments = [
          {
            checkNumber: this._parsedCheckNumber || '',
            checkDate: '',
            amount: this._parsedAmount || '',
            paymentStatus: '0',
            paymentMethod: '1',
          },
        ];
      }
      this.currentPaymentIndex = 0;
      this.syncCurrentPayment();
      this.$nextTick(() => {
        this.focusReceivePaymentPrimaryField();
      });
    },

    /**
     * After person select (or multi-check nav): if check # was prefilled from
     * the document description, focus date; otherwise focus check number.
     */
    focusReceivePaymentPrimaryField() {
      const hasPrefillCheck =
        typeof this.checkNumber === 'string' && this.checkNumber.trim() !== '';
      const target = hasPrefillCheck
        ? this.$refs.checkDate
        : this.$refs.checkNumber;
      if (target && typeof target.focus === 'function') {
        target.focus();
      }
    },

    highlight(text) {
      // Highlight search terms in the results
      const regex = new RegExp(`(${this.searchTerm})`, 'gi');
      return text.replace(regex, '<span class="highlight">$1</span>');
    },

    startReceivePaymentDrag(event) {
      if (event.button !== 0) {
        return;
      }

      const dialog = this.$refs.receivePaymentDialog;
      if (!dialog) {
        return;
      }

      const rect = dialog.getBoundingClientRect();
      this.receivePaymentPosition = { top: rect.top, left: rect.left };
      this.receivePaymentDragOffset = {
        x: event.clientX - rect.left,
        y: event.clientY - rect.top,
      };
      this.isReceivePaymentDragging = true;
      document.body.style.userSelect = 'none';
      window.addEventListener('mousemove', this.onReceivePaymentDrag);
      window.addEventListener('mouseup', this.stopReceivePaymentDrag);
    },

    onReceivePaymentDrag(event) {
      if (!this.isReceivePaymentDragging) {
        return;
      }

      const dialog = this.$refs.receivePaymentDialog;
      if (!dialog) {
        return;
      }

      const maxLeft = Math.max(0, window.innerWidth - dialog.offsetWidth);
      const maxTop = Math.max(0, window.innerHeight - dialog.offsetHeight);
      const left = Math.min(
        maxLeft,
        Math.max(0, event.clientX - this.receivePaymentDragOffset.x),
      );
      const top = Math.min(
        maxTop,
        Math.max(0, event.clientY - this.receivePaymentDragOffset.y),
      );

      this.receivePaymentPosition = { top, left };
    },

    stopReceivePaymentDrag() {
      if (!this.isReceivePaymentDragging) {
        return;
      }

      this.isReceivePaymentDragging = false;
      document.body.style.userSelect = '';
      window.removeEventListener('mousemove', this.onReceivePaymentDrag);
      window.removeEventListener('mouseup', this.stopReceivePaymentDrag);
    },

    closeModal() {
      // Close the modal and reset the fields
      this.stopReceivePaymentDrag();
      this.isSearchFocused = false;
      this.showReceivePayment = false;
      this.searchTerm = '';
      this.searchResults = [];
      this.searchResultsMode = null;
      this.includePreviousTenants = false;
      this.selectedResult = null;
      this.checkNumber = '';
      this.checkDate = '';
      this.amount = '';
      this.paymentStatus = '0';
      this.paymentMethod = '1';
      // Reset multi-check state
      this.payments = [];
      this.currentPaymentIndex = 0;
    },

    // Multi-check navigation methods
    saveCurrentPayment() {
      // Save current input values to the payments array
      if (this.payments.length > 0) {
        this.payments[this.currentPaymentIndex] = {
          checkNumber: this.checkNumber,
          checkDate: this.checkDate,
          amount: this.amount,
          paymentStatus: this.paymentStatus,
          paymentMethod: this.paymentMethod,
        };
      }
    },

    syncCurrentPayment() {
      // Load the current payment into the input fields
      if (this.payments.length > 0 && this.payments[this.currentPaymentIndex]) {
        const payment = this.payments[this.currentPaymentIndex];
        this.checkNumber = payment.checkNumber;
        this.checkDate = payment.checkDate;
        this.amount = payment.amount;
        this.paymentStatus = payment.paymentStatus || '0';
        this.paymentMethod = payment.paymentMethod;
      }
    },

    propagatePaymentStatusAndMethod() {
      this.saveCurrentPayment();
      if (this.currentPaymentIndex !== 0 || this.payments.length <= 1) {
        return;
      }

      const { paymentStatus, paymentMethod, checkDate } = this.payments[0];
      for (let i = 1; i < this.payments.length; i++) {
        this.payments[i].paymentStatus = paymentStatus;
        this.payments[i].paymentMethod = paymentMethod;
        if (checkDate) {
          this.payments[i].checkDate = checkDate;
        }
      }
    },

    prevPayment() {
      if (this.currentPaymentIndex > 0) {
        this.saveCurrentPayment();
        this.currentPaymentIndex--;
        this.syncCurrentPayment();
      }
    },

    advancePaymentOnTab(e) {
      if (this.payments.length > 1) {
        e.preventDefault();
        this.propagatePaymentStatusAndMethod();
        this.currentPaymentIndex =
          (this.currentPaymentIndex + 1) % this.payments.length;
        this.syncCurrentPayment();
        this.$nextTick(() => {
          this.focusReceivePaymentPrimaryField();
        });
      }
    },

    nextPayment() {
      this.saveCurrentPayment();
      if (this.currentPaymentIndex === this.payments.length - 1) {
        // Add a new check
        this.payments.push({
          checkNumber: '',
          checkDate: '',
          amount: '',
          paymentStatus: '0',
          paymentMethod: '1',
        });
      }
      this.currentPaymentIndex++;
      this.syncCurrentPayment();
      this.$nextTick(() => {
        this.focusReceivePaymentPrimaryField();
      });
    },

    removeCurrentPayment() {
      if (this.payments.length > 1) {
        this.payments.splice(this.currentPaymentIndex, 1);
        if (this.currentPaymentIndex >= this.payments.length) {
          this.currentPaymentIndex = this.payments.length - 1;
        }
        this.syncCurrentPayment();
      }
    },

    removeIconFocus() {
      // Find the calendar icon and remove it from the tabbing order
      this.$nextTick(() => {
        const dateIcon = this.$refs.checkDate.nextElementSibling; // Assuming the icon is the next sibling
        if (dateIcon) {
          dateIcon.setAttribute('tabindex', '-1'); // Prevent it from getting focus
        }
      });
    },
    updateDebouncedSearchQuery: debounce(function () {
      this.debouncedSearchQuery = this.searchQuery.trim().toLowerCase();
    }, 300), // 300ms debounce

    // copy selected fields from the document properties select boxes
    copyFields() {
      this.copiedProperties = {};

      if (this.copyCategory) {
        this.copiedProperties.Category = this.formData.Category;
      }
      if (this.copyGroup) {
        this.copiedProperties.Group = this.formData.Group;
      }
      if (this.copySource) {
        this.copiedProperties.Source = this.formData.Source;
      }
      if (this.copyType) {
        this.copiedProperties.Type = this.formData.Type;
      }
    },

    // paste copied fields to the document properties select boxes
    pasteFields() {
      if (this.copyCategory && this.copiedProperties.Category !== undefined) {
        this.formData.Category = this.copiedProperties.Category;
      }
      if (this.copyGroup && this.copiedProperties.Group !== undefined) {
        this.formData.Group = this.copiedProperties.Group;
      }
      if (this.copySource && this.copiedProperties.Source !== undefined) {
        this.formData.Source = this.copiedProperties.Source;
      }
      if (this.copyType && this.copiedProperties.Type !== undefined) {
        this.formData.Type = this.copiedProperties.Type;
      }

      // Save the pasted fields to the database
      this.saveFormData();

      // Update the document in the importedDocs array
      const docIndex = this.importedDocs.findIndex(
        (doc) => doc.Id === this.selectedDocument.Id,
      );

      if (docIndex !== -1) {
        this.importedDocs[docIndex] = {
          ...this.importedDocs[docIndex], // Keep existing fields
          ...this.formData, // Update with the pasted field values
        };
      }
    },

    // Function to connect to WebSocket server
    connectSocket() {
      try {
        const socketUrl = process.env.VUE_APP_SOCKET_URL;

        if (!socketUrl) {
          console.error('Socket URL is undefined.');
          return;
        }

        const token = this.getAuthToken();

        this.socket = io(socketUrl, {
          query: {
            token: token,
            source: 'docuScan',
          },
        });

        this.socket.on('connect', () => {
          console.log('DocScan: Connected to WebSocket server');
        });

        /*
        this.socket.onAny((event, ...args) => {
          console.log(`Received event: ${event}`, args);
        });
        */

        this.socket.on('taskCompleted', (data) => {
          if (data.script === 'imapImportPDFs.js') {
            // console.log('Task completed:', data);

            if (data.code === 0) {
              // Task completed successfully - close and select newest document
              this.showToolsPage = false;
              this.handleToolsPageClose();
            } else {
              // Task had errors - just refresh the list without closing
              this.fetchDocuments(
                0,
                this.selectedType === 'leases'
                  ? 10
                  : this.selectedType === 'both'
                    ? '-'
                    : 0,
              );
            }
          }
        });

        this.socket.on('documentDeleted', (documentId) => {
          const index = this.importedDocs.findIndex(
            (doc) => doc.id === documentId[0],
          );
          if (index !== -1) {
            this.importedDocs.splice(index, 1);
          }
        });

        this.socket.on('reminderUpdated', (document) => {
          this.updateDocument(document);
        });

        this.socket.on('documentUpdated', (document) => {
          this.updateDocument(document);
        });

        this.socket.on('disconnect', () => {
          console.log('Disconnected from WebSocket server');
        });
      } catch (error) {
        console.error('Error connecting to WebSocket server:', error);
      }
    },

    updateDocument(document) {
      // console.log('updateDocument:', document);
      const index = this.importedDocs.findIndex(
        (doc) => doc.Id === document.id,
      );
      if (index !== -1) {
        // Replace the existing document with the updated document
        this.importedDocs.splice(index, 1, document);
      }

      // Update formData fields with new document data
      // Only do this if this.selectedDocument is the same as the updated document
      // from the socket AND if we're not currently in the middle of saving
      // (i.e., selectedDocumentAfterSave is not set, which indicates a save is in progress)
      if (
        this.selectedDocument &&
        this.selectedDocument.Id === document.id &&
        !this.selectedDocumentAfterSave // Don't overwrite if we're waiting to select next document
      ) {
        this.formData = {
          Title: document.Title || '',
          Description: document.Description || '',
          Category: document.Category || null,
          Group: document.Group || null,
          Source: document.Source || null,
          Type: document.Type || null,
          Note: document.Note || '',
        };

        // Also update documentReminders if they exist
        if (document.completedOn !== undefined) {
          this.documentReminders.completedOn = document.completedOn;
        }
        if (document.dueBy !== undefined) {
          this.documentReminders.dueBy = document.dueBy;
        }
      }
    },

    getAuthToken() {
      return localStorage.getItem('token');
    },
  },

  mounted() {
    // Add to mounted() method
    // Set up interval to check for pulsate reminder time
    this.pulsationInterval = setInterval(() => {
      this.checkReminderPulsation();
    }, 30000); // Check every 30 seconds

    // Initial check in case the page loads right at 11:00 AM
    this.checkReminderPulsation();
    this.connectSocket();

    this.updateCheckInterval = setInterval(() => {
      this.checkForUpdates();
    }, 30000); // 30,000ms = 30 seconds

    // Initialize docLastUpdatedOn when the component is mounted
    this.checkForUpdates(); // Run the check immediately after mount

    this.selectedType = localStorage.getItem('docType');

    this.loadDropdownOptions();
    // Pre-fill the search box from the URL query parameter
    const urlParams = new URLSearchParams(window.location.search);
    const searchParam = urlParams.get('search');
    if (searchParam) {
      this.selectedType = 'both';
      localStorage.setItem('docType', 'both');
      this.searchQuery = searchParam; // Set the search query to the value from the URL
      this.expandedSearch = true; // Expand the search box
    }

    // Check for importdocs parameter to open download panel
    const importDocsParam = urlParams.get('importdocs');
    if (importDocsParam === 'true') {
      // Wait for next tick to ensure component is fully mounted
      this.$nextTick(() => {
        this.runDownload({ stopPropagation: () => {} });
      });
    }
  },

  beforeUnmount() {
    // Add to beforeUnmount() method
    if (this.pulsationInterval) {
      clearInterval(this.pulsationInterval);
    }
    if (this.updateCheckInterval) {
      clearInterval(this.updateCheckInterval);
    }
    eventBus.off('select-document', this.handleSelectDocument);
    eventBus.off('update:showReminderDialog');

    this.stopReceivePaymentDrag();
    window.removeEventListener('keydown', this.handleKeyDown);
    clearInterval(this.timer); // Clear the interval timer
  },

  watch: {
    hideStickyNotesByDefault(newVal) {
      if (newVal) {
        this.showStickyNote = false;
      } else {
        this.showStickyNote = true;
      }
    },

    searchQuery() {
      this.updateDebouncedSearchQuery();
    },

    async footerMessage(newVal) {
      if (newVal) {
        // Set a timeout to clear the footerMessage after 10 seconds (10,000 ms)
        setTimeout(() => {
          this.footerMessage = '';
        }, 10000); // 10 seconds in milliseconds
      }
    },
    documentReminders: {
      handler: debounce(function () {
        // console.log('saving reminder in watch:');
        this.saveReminder(); // Call the saveReminders function after the debounce
      }, 1000), // 1000ms debounce delay
      deep: true, // Enable deep watching for nested objects
    },

    filteredDocs(newDocs) {
      if (!this.autoSelected && newDocs.length === 1) {
        // If there's exactly one result, automatically select it
        this.selectedDocument = newDocs[0];
        this.selectDocument(this.selectedDocument);

        // Set the flag to true to prevent further auto-selection
        this.autoSelected = true;
      }
    },

    filteredPeopleProperties(newResults) {
      if (
        this.showReceivePayment &&
        !this.selectedResult &&
        newResults.length === 1
      ) {
        this.selectResult(newResults[0]);
      }
    },

    showToolsPage(isOpen) {
      if (isOpen) {
        this.$nextTick(() => this.initToolsPageDrag());
      } else if (this.toolsPageDragCleanup) {
        this.toolsPageDragCleanup();
        this.toolsPageDragCleanup = null;
      }
    },
  },
};
</script>

<style scoped>
.document-scan {
  width: 1630px;
  margin-left: 20px;
  padding: 20px;
  border: 1px solid #ccc;
  border-radius: 8px;
  padding-top: 10px;
}

.header {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.header-title {
  font-size: 1.1em;
  font-weight: bold;
  color: #333;
}

.toggle-buttons {
  display: flex;
  gap: 10px;
}

.toggle-buttons button {
  background-color: #e0e0e0;
  border: 1px solid #ccc;
  cursor: pointer;
  border-radius: 4px;
  padding: 1px 12px 1px 12px;
}

.toggle-buttons button.active {
  background-color: #a080a8;
  color: white;
}

.content-wrapper {
  display: flex;
  justify-content: space-between;
  gap: 10px;
}

.left-panel {
  flex: 0 0 680px;
}

.right-panel {
  flex: 0 0 900px;
  position: relative;
  min-height: 400px;
}

.pdf-loading-overlay {
  position: absolute;
  inset: 0;
  z-index: 5;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #f3f3f3;
}

.pdf-loading-overlay .fa-spinner {
  font-size: 5em;
  color: #c0c0c0;
}

.document-list {
  max-height: 340px;
  min-height: 340px;
  overflow-y: hidden;
  font-size: 1em;
  width: 680px;
  border-radius: 6px;
  border: 1pt solid #ddd;
  position: relative;
  transition: border-color 0.3s ease;
}

.document-list.drag-over {
  border: 2px dashed #007bff;
  background-color: rgba(0, 123, 255, 0.1);
}

.drag-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 123, 255, 0.2);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  border-radius: 6px;
}

.drag-message {
  background: white;
  padding: 20px;
  border-radius: 8px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  text-align: center;
  font-size: 1.1em;
  color: #007bff;
}

.drag-message i {
  font-size: 2em;
  display: block;
  margin-bottom: 10px;
}

.outer-wrapper {
  overflow-x: hidden;
  max-width: 693px;
}

.inner-wrapper {
  overflow-y: scroll;
  height: 341px;
  width: 696px;
}

table {
  width: 100%;
  cursor: pointer;
  font-size: 10pt;
  border-spacing: 1px;
  user-select: none;
  border-collapse: collapse;
}

table th,
table td {
  padding: 1px;
  padding-left: 6px;
  border-left: 1px solid #dedede;
  border-bottom: 1px solid #eae9e9;
  border-top: none;
  border-right: none;
  text-align: left;
  font-family: consolas;
  font-size: 0.85em;
}

table th {
  background-color: #696969;
  color: #eee;
  padding-top: 4px;
  padding-bottom: 4px;
  padding-right: 3px;
}

table .lighter-text {
  color: #aaa; /* Use a light color code */
}

.table-ellipsis {
  white-space: nowrap; /* Prevent the text from wrapping */
  overflow: hidden; /* Hide overflow content */
  text-overflow: ellipsis; /* Display ellipsis (...) when text overflows */
  padding-right: 5px;
}

table td .ellipsis {
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

table tbody tr:nth-child(odd) {
  background-color: #f2f2f2;
}

table tbody tr:nth-child(even) {
  background-color: #cecccc;
}

table tbody tr:hover {
  background-color: #dfadeb;
}

/* Flexbox for form layout */
.document-form {
  display: flex;
  flex-direction: column;
  background-color: #abaaaa;
  border: 1px solid #ddd;
  padding: 10px;
  margin-top: 10px;
  position: relative;
  font-size: 0.9em;
  border-radius: 4px;
  padding-bottom: 30px;
}

.form-row {
  display: flex;
  justify-content: space-between;
  gap: 0px; /* Add spacing between left and right columns */
}

/* Left column for Title and Description */
.form-column-left {
  flex: 1;
  display: flex;
  flex-direction: column;
}

/* Right column for Dropdowns */
.form-column-right {
  flex: 1;
  display: flex;
  flex-direction: column;
}

/* Grid layout for dropdowns */
.form-group-grid {
  display: grid;
  grid-template-columns: 1fr 1fr; /* Two equal-width columns */
  gap: 10px; /* Spacing between grid items */
}

.form-group-grid select {
  height: 30px;
  border-radius: 4px;
  border: 1pt solid #ccc;
}

.form-group {
  display: flex;
  flex-direction: column;
  margin-bottom: 5px;
}

.form-group label {
  margin-bottom: 2px;
  font-weight: bold;
  font-size: 0.8em;
}

textarea {
  width: 92%;
  padding: 5px;
}

/* Full-width Note Textarea */
.form-note textarea {
  width: 98%; /* Ensure the note spans the full width */
  height: 50px;
  padding: 5px;
  font-size: 0.9em;
  border: 1px solid #ccc;
  border-radius: 4px;
}

.action-button-container {
  position: absolute;
  bottom: 5px;
  right: 6px;
}

.action-button {
  border: 1pt solid #ccc;
  margin-right: 5px;
  padding: 2px;
  border-radius: 4px;
  width: 20px;
  height: 20px;
}

.action-button:hover {
  background: #a080a8;
}

#pdf-frame {
  min-height: 849px;
  border-radius: 6px;
}

.footer-message {
  transition: background-color 0.5s ease; /* Smooth transition for the background fade */
  position: fixed; /* Position it absolutely relative to the parent container */
  border-radius: 4px;
  border: none;
  bottom: 0; /* Align it to the bottom of the parent container */
  left: 0;
  width: 100vw; /* Make it span the full width of the parent container */
  height: 20px; /* Set the desired height */
  text-align: left; /* Optional: Center text horizontally */
  line-height: 20px; /* Optional: Center text vertically within the 20px height */
  font-size: 0.8em;
  padding: 7px;
  padding-left: 20px;
  user-select: text !important;
  z-index: 1000; /* Ensure the footer stays on top of other elements */
}

.footer-message-visible {
  background-color: #fff3cd;
  border: 1px solid #ffc107;
}
.selected-row {
  background-color: #a080a8 !important;
}

.search-bar {
  margin-bottom: 5px;
  margin-top: 5px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 4px;
}

.sticky-note {
  position: absolute;
  font-size: 0.8em;
  top: 50px;
  left: 50px;
  max-width: 255px;
  min-width: 170px;
  height: 170px;
  background-color: #eded94e6;
  z-index: 1000;
  padding: 10px;
  border: 1px solid #dfd3d3;
  box-shadow: 2px 2px 8px rgba(0, 0, 0, 0.3);
  cursor: pointer;
  -webkit-user-select: none;
  -moz-user-select: none;
  user-select: none;
  overflow-y: auto;
  padding-top: 23px;
}

.sticky-note .pin {
  position: absolute;
  top: 4px;
  right: 1px;
  background-color: transparent;
  color: none;
  border: none;
  /* color: transparent; */
}

.overlay {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.3); /* Semi-transparent overlay */
  z-index: 999; /* Just below the sticky note */
  cursor: pointer;
}

.pdf-viewer {
  position: relative;
}

.suggestions-box {
  position: absolute;
  left: 60px;
  right: 0;
  background-color: rgba(0, 0, 0, 0.6); /* Semi-transparent background */
  color: white;
  padding: 10px;
  border-radius: 5px;
  max-height: 200px;
  max-width: 500px;
  font-size: 0.9em;
  overflow-y: hidden;
  z-index: 9999; /* Ensure it floats above other elements */
}

.suggestion-item {
  padding: 3px 0;
  font-size: 0.9em;
  cursor: pointer;
  user-select: none;
}

.suggestion-item:last-child {
  border-bottom: none;
}

:deep(.highlight) {
  color: #e7a4f1;
}

.search-help {
  user-select: none;
  cursor: pointer;
  position: absolute;
  left: 150px;
  top: 150px;
  z-index: 6000;
  width: 650px;
  box-shadow: 9px 13px 7px 4px rgb(0 0 0 / 22%);
  padding: 10px;
  background: #d5d5d5;
  border-radius: 4px;
  border: 1pt solid #555;
  font-family: Arial, sans-serif;
}

.search-help .table-wrapper {
  overflow-y: auto;
}

.search-help table {
  width: 97%;
  border-collapse: collapse;
  font-size: 0.8em;
  font-family: consolas;
  margin-left: 10px;
}

.search-help th,
.search-help td {
  padding: 8px 10px;
  text-align: left;
  border-bottom: 1px solid #ccc;
}

.search-help thead td {
  font-weight: bold;
  background-color: #4b0082;
  color: white;
}

.search-help tbody tr:nth-child(even) {
  background-color: #f0f0f0;
}

.search-help tbody tr:hover {
  background-color: #ddd;
}

.search-help td {
  word-wrap: break-word;
  max-width: 150px;
}

.search-help th {
  background-color: #444;
  color: white;
  font-weight: bold;
}

.search-help table td:first-child {
  font-weight: bold;
}

.search-help td {
  padding: 6px;
  border-bottom: 1px solid #ddd;
}

.search-help .tips {
  margin-bottom: 4px;
  margin-top: -10px;
  margin-left: -10px;
  margin-right: -10px;
  padding: 8px;
  background: #333;
  border-top-left-radius: 4px;
  border-top-right-radius: 4px;
  color: #fff;
  font-size: 1.6em;
  text-align: center;
}
/* Style for the close button */
.close-button {
  position: absolute;
  bottom: 10px;
  right: 18px;
}

/* Overlay for the semi-transparent background */
.help-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  background-color: rgba(0, 0, 0, 0.5); /* Semi-transparent background */
  z-index: 5000;
  display: flex;
  justify-content: center;
  align-items: center;
}

.tools-page-wrapper {
  position: fixed;
  top: 115px;
  left: 200px;
  width: 800px;
  height: 77%;
  background-color: #cdcdcd;
  border: 1px solid #888;
  box-shadow: 8px 13px 8px 6px rgba(0, 0, 0, 0.1);
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  align-items: center;
  overflow: hidden;
  padding: 20px;
  border-radius: 6px;
  z-index: 9999;
}

.tools-page-drag-handle {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  margin-bottom: 10px;
  width: 100%;
  padding: 6px 10px;
  text-align: center;
  cursor: move;
  user-select: none;
  border-bottom: 1px solid #aaa;
  flex-shrink: 0;
}

.tools-page-drag-handle i {
  opacity: 0.55;
}

.reminder-form {
  display: flex;
  flex-direction: column;
  background-color: #abaaaa;
  border: 1px solid #ddd;
  padding: 10px;
  margin-top: 10px;
  position: relative;
  font-size: 0.9em;
  border-radius: 4px;
  min-height: 140px;
}

.reminder-section {
  display: flex;
  justify-content: space-between;
}

.reminder-column {
  flex: 1;
  display: flex;
  flex-direction: column;
  margin-right: 10px;
}

.reminder-column label {
  margin-bottom: 2px;
  font-weight: bold;
  font-size: 0.8em;
}

.reminder-column input,
.reminder-column select {
  margin-bottom: 15px;
  padding: 5px;
  font-size: 1em;
  border: 1px solid #ccc;
  border-radius: 4px;
}
.reminder-column input[type='date'] {
  height: 18px;
}

/* Strikethrough effect for dueBy column */
.strikethrough {
  text-decoration: line-through;
}

.help-close {
  position: absolute;
  right: 17px;
  bottom: 16px;
}
.document-list thead {
  position: sticky;
  top: 0;
}

.active-border {
  border: 2pt solid #e4bded;
  background-color: #a080a8;
  border-radius: 2px;
}

.toolbar-button {
  position: relative; /* Needed to position the notification dot correctly */
  height: 23px;
  width: 85px;
}

.notification-dot {
  box-shadow: 2px 2px 8px rgba(0, 0, 0, 0.3);
  position: relative;
  top: -9px;
  right: -17px;
  width: 19px;
  height: 14px;
  background-color: red;
  border-radius: 50%;
  display: inline-block;
  font-size: 0.7em;
  padding-top: 4px;
  color: white;
}

.link-dialog-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1001;
}

.link-dialog-content {
  background-color: #edebeb;
  padding: 20px;
  border-radius: 8px;
  box-shadow: 7px 7px 8px 3px rgb(0 0 0 / 24%);
  z-index: 2000;
  width: 275px;
  border: 1pt solid #9f9f9f;
  font-size: 0.9em;
}
.link-dialog-content .modal-title {
  margin-bottom: 24px;
  margin-top: -20px;
  margin-left: -20px;
  margin-right: -20px;
  padding: 8px;
  background: #88718b;
  border-top-left-radius: 4px;
  border-top-right-radius: 4px;
  color: #fff;
  font-size: 1.2em;
  text-align: center;
}

.link-dialog-buttons {
  display: flex;
  justify-content: center; /* Centers the buttons horizontally */
  gap: 10px; /* Adds spacing between the buttons */
  margin-top: 20px; /* Adds a margin to create space between content and buttons */
}

.link-dialog-buttons button {
  padding: 10px 20px;
  background-color: #a080a8;
  color: white;
  border: 1pt solid #333;
  border-radius: 4px;
  cursor: pointer;
}

.link-dialog-buttons button:hover {
  background-color: #b28fbb;
}

.doc-email-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.45);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1002;
}

.doc-email-content {
  background-color: #edebeb;
  width: 420px;
  border-radius: 8px;
  border: 1pt solid #9f9f9f;
  box-shadow: 7px 7px 8px 3px rgb(0 0 0 / 24%);
  padding: 20px;
}

.doc-email-content .modal-title {
  margin-bottom: 18px;
  margin-top: -20px;
  margin-left: -20px;
  margin-right: -20px;
  padding: 8px;
  background: #88718b;
  border-top-left-radius: 6px;
  border-top-right-radius: 6px;
  color: #fff;
  font-size: 1.1em;
  text-align: center;
}

.doc-email-row {
  display: flex;
  flex-direction: column;
  margin-bottom: 10px;
}

.doc-email-label-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
}

.doc-email-property-btn {
  border: 1px solid #888;
  font-size: 0.72em;
  line-height: 1;
  padding: 3px 8px;
  border-radius: 3px;
  cursor: pointer;
  margin-bottom: 5px;
  background: #a080a8;
  color: white;
}

.doc-email-property-btn.active {
  background: #88718b;
  border-color: #6d5870;
  color: #fff;
}

.doc-email-row label {
  font-size: 0.85em;
  margin-bottom: 4px;
  font-weight: bold;
}

.doc-email-row input,
.doc-email-row textarea {
  width: 100%;
  border: 1px solid #bbb;
  border-radius: 4px;
  padding: 7px;
  box-sizing: border-box;
  font-family: inherit;
}

.doc-email-property-results {
  margin-top: 6px;
  border: 1px solid #c7c7c7;
  border-radius: 4px;
  background: #fff;
  overflow: hidden;
}

.doc-email-property-result {
  display: block;
  width: 100%;
  text-align: left;
  border: none;
  border-bottom: 1px solid #ededed;
  background: #fff;
  padding: 6px 8px;
  font-size: 0.85em;
  cursor: pointer;
}

.doc-email-property-result:last-child {
  border-bottom: none;
}

.doc-email-property-result:hover {
  background: #f3edf6;
}

.doc-email-buttons {
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  margin-top: 14px;
}

.doc-email-buttons button {
  padding: 8px 12px;
  border: 1pt solid #777;
  border-radius: 4px;
  cursor: pointer;
  min-width: 64px;
}

.doc-email-button-spinner {
  display: inline-block;
  width: 14px;
  height: 14px;
  border: 2px solid #bbb;
  border-top-color: #555;
  border-radius: 50%;
  animation: doc-email-spin 0.7s linear infinite;
}

@keyframes doc-email-spin {
  to {
    transform: rotate(360deg);
  }
}

.doc-email-error-message {
  font-size: 0.95em;
  margin-bottom: 6px;
}

.pdf-placeholder {
  border: 1pt solid #ddd;
  padding: 50px 100px;
  height: 742px;
  border-radius: 4px;
}

.pdf-placeholder-content {
  text-align: center;
  color: #666;
  padding-top: 200px;
}

.icon-container {
  padding: 10px;
  display: inline-block;
  margin-bottom: 20px;
}

.pdf-placeholder-content .pdf-icon {
  font-size: 4em;
  color: #888;
}

.pdf-placeholder-content p {
  font-size: 2em;
  margin: 0;
  color: #666;
  margin-left: 110px;
  margin-right: 110px;
}

/* Modal overlay to cover the whole page and dim background */
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  background-color: rgba(0, 0, 0, 0.5); /* Semi-transparent background */
  display: flex;
  justify-content: center; /* Centers horizontally */
  align-items: center; /* Centers vertically */
  z-index: 1000; /* Ensure it is above other elements */
}

/* Modal content box */
.modal-content {
  background-color: white;
  padding: 20px;
  border-radius: 10px;
  box-shadow: 6px 7px 7px 4px rgba(0, 0, 0, 0.25);
  width: 300px; /* Set the width of the modal */
  max-width: 80%; /* Responsive for smaller screens */
  z-index: 1001;
}

/* Styling for modal buttons */
.modal-actions {
  margin-top: 20px;
  text-align: right;
}

.modal-actions button {
  margin-left: 10px;
  padding: 8px 12px;
  background-color: #a080a8;
  color: white;
  border: none;
  border-radius: 4px;
  cursor: pointer;
}

.modal-actions button:hover {
  background-color: #907793;
}

/* Input styling */
.modal-content input[type='text'] {
  width: 100%;
  padding: 8px;
  margin-top: 10px;
  border: 1px solid #ccc;
  border-radius: 4px;
  box-sizing: border-box;
}

.clear-button {
  cursor: pointer;
  margin-left: 3px;
  margin-left: 3px;
  border-radius: 5px;
  border: 1pt solid #888;
  height: 26px;
  width: 30px;
  color: #777;
}
/* receive payment */

.receive-payment .modal {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  display: flex;
  justify-content: left; /* Align content to the left */
  align-items: flex-start; /* Align content at the top */
}

.receive-payment .modal-content {
  background-color: #d7d7d7;
  padding: 10px;
  border-radius: 5px;
  width: 600px;
  font-size: 0.85em;
  height: 295px;
  z-index: 2001;
  position: absolute;
  top: 570px;
  left: 828px;
}

.receive-payment .modal-title {
  margin-bottom: 4px;
  margin-top: -10px;
  margin-left: -10px;
  margin-right: -10px;
  padding: 8px;
  background: #333;
  border-top-left-radius: 4px;
  border-top-right-radius: 4px;
  color: #fff;
  font-size: 1.6em;
  text-align: center;
  cursor: move;
  user-select: none;
}

.receive-payment .modal-background {
  position: absolute;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.2);
  z-index: 2000;
}

.receive-payment .modal-content input {
  width: 80%;
  padding: 8px;
  margin-top: 4px;
  border: 1px solid #ccc;
  border-radius: 4px;
  box-sizing: border-box;
}
.receive-payment .modal-content input#checkNumber {
  width: 110px;
}
.receive-payment .modal-content input[type='date'] {
  width: 100%;
  padding: 7px;
  margin-top: 4px;
  border: 1px solid #ccc;
  border-radius: 4px;
  box-sizing: border-box;
}

.receive-payment .modal-content select {
  width: 100%;
  padding: 7px;
  margin-top: 4px;
  border: 1px solid #ccc;
  border-radius: 4px;
  box-sizing: border-box;
}

.receive-payment .search-row {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 10px;
}

.receive-payment .search-box {
  width: 150px;
  padding: 5px;
  flex: 1;
  min-width: 110px;
}

.receive-payment .previous-tenant-btn {
  flex-shrink: 0;
  height: 28px;
  padding: 0 8px;
  border: 1px solid #ccc;
  border-radius: 4px;
  background: #f5f5f5;
  color: #333;
  font-size: 0.85em;
  cursor: pointer;
  white-space: nowrap;
}

.receive-payment .previous-tenant-btn.active {
  background: #8955a1;
  border-color: #6f3f86;
  color: #fff;
}

.receive-payment .previous-tenant-btn:hover {
  border-color: #8955a1;
}

.receive-payment .search-results {
  height: 80px;
  overflow-y: auto;
}

.receive-payment .result-item {
  padding: 5px;
  cursor: pointer;
}

/* Check navigation for multi-check support */
.receive-payment .check-navigation {
  display: flex;
  align-items: center;
  gap: 8px;
}

.receive-payment .nav-arrow {
  background: #8955a1;
  color: white;
  border: none;
  border-radius: 4px;
  width: 28px;
  height: 28px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.9em;
  transition:
    background-color 0.2s,
    opacity 0.2s;
}

.receive-payment .nav-arrow:hover:not(:disabled) {
  background: #6e4580;
}

.receive-payment .nav-arrow:disabled {
  opacity: 0.4;
  cursor: not-allowed;
}

.receive-payment .check-indicator {
  font-weight: bold;
  font-size: 0.9em;
  color: #333;
  display: flex;
  align-items: center;
  gap: 5px;
  white-space: nowrap;
}

.receive-payment .remove-check-btn {
  background: transparent;
  border: none;
  color: #c44;
  cursor: pointer;
  padding: 4px;
  font-size: 0.85em;
}

.receive-payment .remove-check-btn:hover {
  color: #a00;
}

/* Arrange inputs in a row */
.receive-payment .payment-inputs {
  display: flex;
  flex-direction: row; /* Horizontal row layout for inputs */
  justify-content: space-between; /* Evenly space items */
  gap: 10px; /* Spacing between inputs */
  margin-bottom: 20px; /* Space between inputs and buttons */
}

.receive-payment .input-group {
  display: flex;
  flex-direction: column; /* Ensure label is above the input */
  flex: 1; /* Allow inputs to grow equally */
}

/* Action buttons positioned below the inputs */
.receive-payment .action-buttons {
  display: flex;
  justify-content: right; /* Align buttons with space between */
  gap: 10px; /* Space between buttons */
}

.receive-payment .action-buttons button {
  padding: 10px 20px;
  background-color: #8955a1;
  color: white;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  box-shadow: 2px 2px 8px rgba(0, 0, 0, 0.3);
}

.receive-payment .action-buttons button:hover {
  background-color: #45a049;
}

.receive-payment .modal-close {
  position: absolute;
  top: 10px;
  right: 10px;
}

.receive-payment :deep(.highlight) {
  background: #e7a4f1;
  color: #333;
}

.receive-payment .selected-result {
  background-color: #a080a8;
}

.modal-active .document-list thead {
  position: static !important; /* Remove sticky behavior when modal is active */
}

.receive-payment input[type='date']::-webkit-calendar-picker-indicator {
  display: none; /* Hides the calendar icon, thus removing the focus issue */
  pointer-events: none; /* Ensures that the icon cannot be clicked */
}

.page-selector {
  margin-left: auto;
  font-size: 0.8em;
}

.page-selector span {
  margin: 5px;
}

.expandedSearch {
  background-color: #a080a8;
}

.copy-checks {
  position: relative;
  top: -3px;
  padding-left: 17px;
  font-size: 0.75em;
  color: #333;
}

.copy-checks input {
  /* Make input occupy full size for interaction */
  position: absolute;
  left: 0;
  top: 0;
  opacity: 0;
  width: 20px; /* Match this with custom checkbox width */
  height: 20px; /* Match this with custom checkbox height */
  cursor: pointer;
  z-index: 3; /* Keeps it above the ::before element */
}

.copy-checks input + .copy-checks-offset::before {
  content: '';
  width: 10px;
  height: 10px;
  border: 1pt solid #888; /* Change border color here */
  border-radius: 3px;
  background-color: #b9b9b9;
  position: absolute;
  left: 0; /* Adjust as needed for spacing */
  top: 0;
  transition: all 0.3s ease;
  z-index: 1; /* Places it under the input */
}

.copy-checks input:checked + .copy-checks-offset::before {
  background-color: #907793;
  border-color: #696869;
}

.copy-checks-offset {
  position: relative;
  padding-left: 15px; /* Space for the custom checkbox */
  display: inline-block;
}

@keyframes pulsate {
  0% {
    border-color: #a9a5a5;
  }
  50% {
    border-color: red;
    box-shadow: 0 0 5px red;
  }
  100% {
    border-color: #a9a5a5;
  }
}

.pulsating {
  animation: pulsate 1s infinite;
}

.property-iframe-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  background: rgba(0, 0, 0, 0.55);
  z-index: 30000;
  display: flex;
  justify-content: center;
  align-items: flex-start;
}

.property-iframe-container {
  width: 90vw;
  max-width: 1400px;
  height: 100vh;
  background: #fff;
  overflow: hidden;
  box-shadow: 0 8px 30px rgba(0, 0, 0, 0.4);
  display: flex;
  flex-direction: column;
}

.property-iframe-title-bar {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 8px 14px;
  background: #333;
  color: #eee;
  font-size: 0.95em;
  flex-shrink: 0;
}

.property-iframe-close-btn {
  background: #555;
  border: 1px solid #777;
  color: #eee;
  border-radius: 50%;
  width: 26px;
  height: 26px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.7em;
}

.property-iframe-close-btn:hover {
  background: #d32f2f;
}

.property-iframe-scroll-wrapper {
  flex: 1;
  overflow: hidden;
  position: relative;
}

.property-iframe-spinner-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  background: #1e1e2e;
  z-index: 1;
  gap: 12px;
  color: #ccc;
  font-size: 1.1em;
}

.property-iframe-spinner-overlay .fa-spinner {
  font-size: 2em;
  color: #4fc3f7;
}

.property-lease-iframe {
  border: none;
  width: 100%;
  height: 100%;
}
</style>
